import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL, APP_VERSION } from './config';
import TouchHandler from './components/TouchHandler';
import SwipeRow from './components/SwipeRow';
import PwaBanners, { NetworkBanner } from './components/PwaBanners';
import ShareLanding from './components/ShareLanding';
import MobileSheetHandle from './components/MobileSheetHandle';
import { saveMainLists, loadCachedMainLists, isOnline } from './offlineStore';

const MAIN_APP_TAB_STORAGE_KEY = 'countryManagerMainTab';
const VALID_MAIN_TABS = ['songs', 'choreographies', 'associations', 'share'];

function readShareTokenFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const t = (params.get('share') || '').trim();
    return t;
  } catch (_) {
    return '';
  }
}

function buildSharePageUrl(token) {
  const url = new URL(window.location.href);
  url.search = '';
  url.hash = '';
  url.searchParams.set('share', token);
  return url.toString();
}

function clearShareTokenFromUrl() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('share')) return;
    url.searchParams.delete('share');
    const next = `${url.pathname}${url.search}${url.hash}` || '/';
    window.history.replaceState({}, '', next);
  } catch (_) {}
}

function readStoredMainAppTab() {
  try {
    const raw = localStorage.getItem(MAIN_APP_TAB_STORAGE_KEY);
    if (raw && VALID_MAIN_TABS.includes(raw)) return raw;
  } catch (_) {}
  return 'songs';
}

function App() {
  const [songs, setSongs] = useState([]);
  const [choreographies, setChoreographies] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsLastAccepted, setTermsLastAccepted] = useState(null);
  const [isSavingTerms, setIsSavingTerms] = useState(false);  // Nuovo stato per tracciare il salvataggio

  const [newSongTitle, setNewSongTitle] = useState('');
  const [newSongArtist, setNewSongArtist] = useState('');
  const [newSongUrl, setNewSongUrl] = useState('');
  const [newSongFile, setNewSongFile] = useState(null);
  const [newSongGenre, setNewSongGenre] = useState('');
  const [newSongNotes, setNewSongNotes] = useState(''); 
  const [editingSong, setEditingSong] = useState(null);

  const [newChoreographyTitle, setNewChoreographyTitle] = useState('');
  const [newChoreographyArtist, setNewChoreographyArtist] = useState('');
  const [newChoreographyDifficulty, setNewChoreographyDifficulty] = useState('');
  const [newChoreographyNotes, setNewChoreographyNotes] = useState(''); 
  const [editingChoreography, setEditingChoreography] = useState(null);
  const [choreographyMediaItems, setChoreographyMediaItems] = useState([]);
  const [removedChoreographyMediaIds, setRemovedChoreographyMediaIds] = useState([]);

  // Modificato: Ora selectedSongIds è un array per la multi-selezione
  const [selectedSongIds, setSelectedSongIds] = useState([]); 
  const [selectedChoreographyId, setSelectedChoreographyId] = useState('');
  const [newAssociationNotes, setNewAssociationNotes] = useState('');
  const [editingAssociation, setEditingAssociation] = useState(null);

  const [errorMessage, setErrorMessage] = useState('');
  const [uploadingSong, setUploadingSong] = useState(false);
  const [uploadingChoreography, setUploadingChoreography] = useState(false);
  
  const [songUploadProgress, setSongUploadProgress] = useState(0);
  const [choreoUploadProgress, setChoreoUploadProgress] = useState(0);

  const [playingMedia, setPlayingMedia] = useState(null);
  const [downloadingMediaKey, setDownloadingMediaKey] = useState(null);

  const confirmModalRef = useRef(null);
  const editSongModalRef = useRef(null);
  const editChoreographyModalRef = useRef(null);
  const playMediaModalRef = useRef(null);
  const editAssociationModalRef = useRef(null);
  const newAssociationModalRef = useRef(null);
  const shareRecipientModalRef = useRef(null);
  const trainingModalRef = useRef(null);
  const sharePressTimerRef = useRef(null);
  const sharePressTriggeredRef = useRef(false);

  const [itemToDelete, setItemToDelete] = useState(null);

  // Stati per la gestione del Login/Registrazione
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [authToken, setAuthToken] = useState(localStorage.getItem('authToken'));
  const [message, setMessage] = useState(null);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [registrationKey, setRegistrationKey] = useState('');
  const [email, setEmail] = useState('');
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [showResetCode, setShowResetCode] = useState(false);
  const [hasSeenCode, setHasSeenCode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  // Stato per il codice di recupero generato
  const [registrationResetCode, setRegistrationResetCode] = useState('');
  const [showRegistrationResetCode, setShowRegistrationResetCode] = useState(false);
  const [registrationMessage, setRegistrationMessage] = useState('');

  // Stati per l'autenticazione e l'utente
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUsername, setCurrentUsername] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Stati per il feedback utente
  const [loading, setLoading] = useState(false);

  // Stati per l'interfaccia utente
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Stato per la modalità scura
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Recupera la preferenza dal localStorage o dal sistema
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Applica la classe dark su <html> e salva la preferenza
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // Stato per la ricerca
  const [searchTerm, setSearchTerm] = useState('');

  // Vista principale: canzoni, coreografie o associazioni (persistita al refresh)
  const [mainAppTab, setMainAppTab] = useState(readStoredMainAppTab);
  const [previousMainAppTab, setPreviousMainAppTab] = useState('songs');
  const [isAppOnline, setIsAppOnline] = useState(() => isOnline());
  const [dataStale, setDataStale] = useState(false);
  const [swUpdateRegistration, setSwUpdateRegistration] = useState(null);
  const [expandedSongMediaIds, setExpandedSongMediaIds] = useState([]);
  const [expandedChoreoMediaIds, setExpandedChoreoMediaIds] = useState([]);
  const [shareViewToken, setShareViewToken] = useState(() => readShareTokenFromUrl());
  const [pendingShareImportAfterLogin, setPendingShareImportAfterLogin] = useState(false);
  const [shareLinkCreating, setShareLinkCreating] = useState(false);
  const [lastShareLinkUrl, setLastShareLinkUrl] = useState('');

  useEffect(() => {
    try {
      localStorage.setItem(MAIN_APP_TAB_STORAGE_KEY, mainAppTab);
    } catch (_) {}
  }, [mainAppTab]);

  useEffect(() => {
    window.__CDH_SET_SW_UPDATE__ = (reg) => setSwUpdateRegistration(reg);
    if (window.__CDH_SW_UPDATE__) {
      setSwUpdateRegistration(window.__CDH_SW_UPDATE__);
      window.__CDH_SW_UPDATE__ = null;
    }
    const onOnline = () => setIsAppOnline(true);
    const onOffline = () => setIsAppOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.__CDH_SET_SW_UPDATE__ = null;
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    setOpenSwipeId(null);
  }, [mainAppTab]);

  // Stato per la modal Disclaimer
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // API_BASE_URL è ora importato da config.js

  // Stato per la sezione Training
  const [showTraining, setShowTraining] = useState(false);
  const [trainingCount, setTrainingCount] = useState(1);
  const [trainingDifficulty, setTrainingDifficulty] = useState('all');
  const [trainingChoreographies, setTrainingChoreographies] = useState([]);
  const [trainingStarted, setTrainingStarted] = useState(false);
  const [cloneTargetUsername, setCloneTargetUsername] = useState('');
  const [cloneSongsSelected, setCloneSongsSelected] = useState(false);
  const [cloneChoreosSelected, setCloneChoreosSelected] = useState(false);
  const [cloneAssociationsSelected, setCloneAssociationsSelected] = useState(true);
  const [cloneIncludeDependencies, setCloneIncludeDependencies] = useState(true);
  const [cloneCopyFiles, setCloneCopyFiles] = useState(true);
  const [cloneSkipExisting, setCloneSkipExisting] = useState(true);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [cloneReport, setCloneReport] = useState(null);
  const [cloneMode, setCloneMode] = useState('all'); // all | selected
  const [cloneSelectedSongIds, setCloneSelectedSongIds] = useState([]);
  const [cloneSelectedChoreoIds, setCloneSelectedChoreoIds] = useState([]);
  const [cloneSelectedAssociationIds, setCloneSelectedAssociationIds] = useState([]);
  const [openSwipeId, setOpenSwipeId] = useState(null);
  const [cloneRequestsInbox, setCloneRequestsInbox] = useState([]);
  const [cloneRequestActionLoading, setCloneRequestActionLoading] = useState(false);

  const openTraining = () => {
    setTrainingChoreographies([]);
    setTrainingStarted(false);
    setShowTraining(true);
    if (trainingModalRef.current && !trainingModalRef.current.open) {
      trainingModalRef.current.showModal();
    }
  };

  const handleCloneUserData = async (apply) => {
    setErrorMessage('');
    setCloneReport(null);

    const sourceUserId = String(user?.id || '');
    if (!sourceUserId || !cloneTargetUsername.trim()) {
      setErrorMessage('Per condividere indica lo username del destinatario.');
      return;
    }

    const hasSongSel = cloneSelectedSongIds.length > 0;
    const hasChoreoSel = cloneSelectedChoreoIds.length > 0;
    const hasAssocSel = cloneSelectedAssociationIds.length > 0;
    const selectedTotal = cloneSelectedSongIds.length + cloneSelectedChoreoIds.length + cloneSelectedAssociationIds.length;

    if (selectedTotal === 0) {
      setErrorMessage('Seleziona almeno un elemento da condividere (pressione lunga sulle tile).');
      return;
    }

    // Tipi derivati automaticamente dagli ID selezionati
    setCloneSongsSelected(hasSongSel);
    setCloneChoreosSelected(hasChoreoSel);
    setCloneAssociationsSelected(hasAssocSel);

    let scope = 'associations_with_dependencies';
    if (hasAssocSel) {
      scope = cloneIncludeDependencies ? 'associations_with_dependencies' : 'associations';
    } else if (hasSongSel && hasChoreoSel) {
      scope = 'songs_and_choreographies';
    } else if (hasSongSel) {
      scope = 'songs';
    } else if (hasChoreoSel) {
      scope = 'choreographies';
    }

    const payload = {
      sourceUserId: Number(sourceUserId),
      targetUsername: cloneTargetUsername.trim(),
      scope,
      copyFiles: Boolean(cloneCopyFiles),
      skipExisting: Boolean(cloneSkipExisting),
      songIds: cloneSelectedSongIds,
      choreographyIds: cloneSelectedChoreoIds,
      associationIds: cloneSelectedAssociationIds
    };

    try {
      setCloneLoading(true);
      if (apply) {
        const data = await fetchData('/api/clone-requests', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        setCloneReport(data?.report || null);
        setMessage({ type: 'success', text: data?.message || 'Richiesta inviata.' });
        if (shareRecipientModalRef.current?.open) shareRecipientModalRef.current.close();
        exitShareSelection();
        return;
      }

      const data = await fetchData('/api/clone-user-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          apply: false
        })
      });
      setCloneReport(data?.report || null);
      setMessage({ type: 'success', text: data?.message || 'Anteprima condivisione completata.' });
    } catch (e) {
      setErrorMessage(e.message || 'Errore durante la condivisione dei dati.');
    } finally {
      setCloneLoading(false);
    }
  };

  const loadCloneRequestsInbox = useCallback(async () => {
    if (!authToken || !isLoggedIn) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/clone-requests/inbox`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      setCloneRequestsInbox(Array.isArray(data?.requests) ? data.requests : []);
    } catch (_) {}
  }, [authToken, isLoggedIn, API_BASE_URL]);

  const handleDecideCloneRequest = async (requestId, accept) => {
    try {
      setCloneRequestActionLoading(true);
      const endpoint = accept ? `/api/clone-requests/${requestId}/accept` : `/api/clone-requests/${requestId}/reject`;
      const data = await fetchData(endpoint, { method: 'POST' });
      setMessage({ type: 'success', text: data?.message || 'Richiesta gestita.' });
      await loadCloneRequestsInbox();
      if (accept) {
        const [songsData, choreoData, assocData] = await Promise.all([
          fetchData('/api/songs'),
          fetchData('/api/choreographies'),
          fetchData('/api/associations')
        ]);
        setSongs(songsData);
        setChoreographies(choreoData);
        setAssociations(assocData);
        setMessage({ type: 'success', text: 'Richiesta accettata. Elenco aggiornato.' });
      }
    } catch (e) {
      setErrorMessage(e.message || 'Errore gestione richiesta di condivisione.');
    } finally {
      setCloneRequestActionLoading(false);
    }
  };

  const resetCloneSelections = () => {
    setCloneSelectedSongIds([]);
    setCloneSelectedChoreoIds([]);
    setCloneSelectedAssociationIds([]);
  };

  const selectedShareCount =
    cloneSelectedSongIds.length + cloneSelectedChoreoIds.length + cloneSelectedAssociationIds.length;

  const clearSharePressTimer = () => {
    if (sharePressTimerRef.current) {
      clearTimeout(sharePressTimerRef.current);
      sharePressTimerRef.current = null;
    }
  };

  const enterShareSelection = (type, id) => {
    const sid = String(id);
    setOpenSwipeId(null);
    setCloneMode('selected');
    if (type === 'song') {
      setCloneSelectedSongIds((prev) => (prev.includes(sid) ? prev : [...prev, sid]));
    } else if (type === 'choreo') {
      setCloneSelectedChoreoIds((prev) => (prev.includes(sid) ? prev : [...prev, sid]));
    } else if (type === 'association') {
      setCloneSelectedAssociationIds((prev) => (prev.includes(sid) ? prev : [...prev, sid]));
    }
  };

  const toggleShareItem = (type, id) => {
    const sid = String(id);
    const toggle = (prev) => (prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]);
    if (type === 'song') setCloneSelectedSongIds(toggle);
    else if (type === 'choreo') setCloneSelectedChoreoIds(toggle);
    else if (type === 'association') setCloneSelectedAssociationIds(toggle);
  };


  const openShareTab = () => {
    setPreviousMainAppTab((prev) => (mainAppTab === 'share' ? prev : mainAppTab));
    closeTraining();
    setMainAppTab('share');
  };

  const closeShareTab = () => {
    const back = previousMainAppTab && previousMainAppTab !== 'share' ? previousMainAppTab : 'songs';
    setMainAppTab(back);
  };

  const exitShareSelection = () => {
    clearSharePressTimer();
    setOpenSwipeId(null);
    setCloneMode('all');
    resetCloneSelections();
    setCloneReport(null);
  };

  const openShareRecipientModal = () => {
    if (selectedShareCount === 0) {
      setErrorMessage('Seleziona almeno un elemento da condividere.');
      return;
    }
    setErrorMessage('');
    setCloneReport(null);
    if (shareRecipientModalRef.current) shareRecipientModalRef.current.showModal();
  };

  const closeShareRecipientModal = () => {
    if (shareRecipientModalRef.current?.open) shareRecipientModalRef.current.close();
  };

  const getPublicShareUrl = (url) => {
    const u = (url || '').trim();
    if (!u) return null;
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    return null;
  };

  const createShareLink = async () => {
    if (selectedShareCount === 0) {
      throw new Error('Seleziona almeno un elemento da condividere.');
    }
    const data = await fetchData('/api/share-links', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        songIds: cloneSelectedSongIds,
        choreographyIds: cloneSelectedChoreoIds,
        associationIds: cloneSelectedAssociationIds,
        copyFiles: Boolean(cloneCopyFiles),
        skipExisting: Boolean(cloneSkipExisting),
        includeDependencies: Boolean(cloneIncludeDependencies)
      })
    });
    if (!data?.token) throw new Error(data?.message || 'Link non creato.');
    const url = buildSharePageUrl(data.token);
    setLastShareLinkUrl(url);
    return { ...data, url };
  };

  const buildExternalShareText = async () => {
    let shareUrl = lastShareLinkUrl;
    try {
      const created = await createShareLink();
      shareUrl = created.url;
    } catch (err) {
      // se fallisce la creazione link, continua con solo il riepilogo
      console.warn('createShareLink:', err);
    }

    const lines = ['Country Dance Hub — elementi condivisi', ''];
    if (shareUrl) {
      lines.push('Apri questo link:');
      lines.push(shareUrl);
      lines.push('');
      lines.push('Se hai l’app: apri il link e premi “Importa”.');
      lines.push('Altrimenti puoi solo visualizzare i contenuti.');
      lines.push('');
    }

    let count = 0;

    cloneSelectedSongIds.forEach((id) => {
      const song = songs.find((s) => String(s.id) === String(id));
      if (!song) return;
      count += 1;
      lines.push(`🎵 ${song.title}${song.artist ? ` — ${song.artist}` : ''}`);
      const link = getPublicShareUrl(song.url);
      if (link) lines.push(`   ${link}`);
      else if (song.url) lines.push('   (media locale: apri il link di condivisione)');
      lines.push('');
    });

    cloneSelectedChoreoIds.forEach((id) => {
      const choreo = choreographies.find((c) => String(c.id) === String(id));
      if (!choreo) return;
      count += 1;
      const meta = [choreo.artist, choreo.difficulty].filter(Boolean).join(' · ');
      lines.push(`💃 ${choreo.title}${meta ? ` — ${meta}` : ''}`);
      const mediaList = getChoreographyMediaList(choreo);
      if (!mediaList.length) {
        lines.push('   (nessun link)');
      } else {
        mediaList.forEach((media, idx) => {
          const label = (media?.label || '').trim() || `Media ${idx + 1}`;
          const link = getPublicShareUrl(media.url);
          if (link) lines.push(`   ${label}: ${link}`);
          else if (media.url) lines.push(`   ${label}: (media locale: apri il link di condivisione)`);
        });
      }
      lines.push('');
    });

    cloneSelectedAssociationIds.forEach((id) => {
      const association = associations.find((a) => String(a.id) === String(id));
      if (!association) return;
      const song = songs.find((s) => s.id === association.songId);
      const choreo = choreographies.find((c) => c.id === association.choreographyId);
      count += 1;
      lines.push(`🔗 ${(song && song.title) || 'Canzone'} → ${(choreo && choreo.title) || 'Coreografia'}`);
      if (song) {
        const songLink = getPublicShareUrl(song.url);
        if (songLink) lines.push(`   Canzone: ${songLink}`);
      }
      if (choreo) {
        getChoreographyMediaList(choreo).forEach((media, idx) => {
          const label = (media?.label || '').trim() || `Coreo media ${idx + 1}`;
          const link = getPublicShareUrl(media.url);
          if (link) lines.push(`   ${label}: ${link}`);
        });
      }
      lines.push('');
    });

    if (!count && !shareUrl) return '';
    if (count) lines.push(`Totale: ${count} element${count === 1 ? 'o' : 'i'}`);
    return lines.join('\n');
  };

  const copyTextToClipboard = async (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  };

  const shareViaWebShare = async (text) => {
    if (!text) return false;
    if (!navigator.share) return false;
    try {
      await navigator.share({
        title: 'Country Dance Hub',
        text,
        url: lastShareLinkUrl || undefined
      });
      return true;
    } catch (err) {
      if (err && err.name === 'AbortError') return true;
      console.warn('navigator.share fallito:', err);
      return false;
    }
  };

  const shareViaWhatsApp = async () => {
    const text = await buildExternalShareText();
    if (!text) {
      setErrorMessage('Seleziona almeno un elemento da condividere.');
      return;
    }
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const copyShareTextToClipboard = async () => {
    try {
      setShareLinkCreating(true);
      const text = await buildExternalShareText();
      if (!text) {
        setErrorMessage('Seleziona almeno un elemento da condividere.');
        return;
      }
      await copyTextToClipboard(text);
      setErrorMessage('');
      setMessage({ type: 'success', text: 'Testo (con link) copiato negli appunti.' });
    } catch (err) {
      console.error('Copia fallita:', err);
      setErrorMessage(err.message || 'Impossibile copiare il testo.');
    } finally {
      setShareLinkCreating(false);
    }
  };

  const copyShareLinkOnly = async () => {
    if (selectedShareCount === 0) {
      setErrorMessage('Seleziona almeno un elemento da condividere.');
      return;
    }
    try {
      setShareLinkCreating(true);
      setErrorMessage('');
      const created = await createShareLink();
      await copyTextToClipboard(created.url);
      setMessage({ type: 'success', text: 'Link di condivisione copiato.' });
    } catch (err) {
      setErrorMessage(err.message || 'Impossibile creare il link.');
    } finally {
      setShareLinkCreating(false);
    }
  };

  const handleExternalShare = async () => {
    if (selectedShareCount === 0) {
      setErrorMessage('Seleziona almeno un elemento da condividere.');
      return;
    }
    setErrorMessage('');
    try {
      setShareLinkCreating(true);
      const text = await buildExternalShareText();
      const shared = await shareViaWebShare(text);
      if (!shared) {
        const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setErrorMessage(err.message || 'Condivisione non riuscita.');
    } finally {
      setShareLinkCreating(false);
    }
  };

  const openShareView = (token) => {
    const t = String(token || '').trim();
    if (!t) return;
    setShareViewToken(t);
  };

  const closeShareView = () => {
    setShareViewToken('');
    clearShareTokenFromUrl();
  };

  const selectAllVisibleForShare = () => {
    setCloneMode('selected');
    if (mainAppTab === 'songs') {
      setCloneSelectedSongIds(filteredSongs.map((s) => String(s.id)));
    } else if (mainAppTab === 'choreographies') {
      setCloneSelectedChoreoIds(filteredChoreographies.map((c) => String(c.id)));
    } else if (mainAppTab === 'associations') {
      const visible = associations.filter((association) => {
        const song = songs.find((s) => s.id === association.songId);
        const choreography = choreographies.find((c) => c.id === association.choreographyId);
        if (!song || !choreography) return false;
        const searchLower = searchTerm.toLowerCase();
        if (!searchLower) return true;
        return (
          song.title.toLowerCase().includes(searchLower) ||
          song.artist.toLowerCase().includes(searchLower) ||
          song.genre?.toLowerCase().includes(searchLower) ||
          choreography.title.toLowerCase().includes(searchLower) ||
          choreography.artist.toLowerCase().includes(searchLower) ||
          choreography.difficulty?.toLowerCase().includes(searchLower) ||
          association.notes?.toLowerCase().includes(searchLower)
        );
      });
      setCloneSelectedAssociationIds(visible.map((a) => String(a.id)));
    }
  };

  const getShareTileHandlers = (type, id) => {
    const sid = String(id);
    return {
      onPointerDown: (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        // Ignora long-press su controlli interattivi interni
        const tag = e.target?.closest?.('button, a, input, textarea, select, audio, video, iframe, label');
        if (tag) return;
        sharePressTriggeredRef.current = false;
        clearSharePressTimer();
        sharePressTimerRef.current = setTimeout(() => {
          sharePressTriggeredRef.current = true;
          enterShareSelection(type, sid);
        }, 480);
      },
      onPointerUp: () => clearSharePressTimer(),
      onPointerCancel: () => clearSharePressTimer(),
      onPointerLeave: () => clearSharePressTimer(),
      onContextMenu: (e) => {
        e.preventDefault();
        enterShareSelection(type, sid);
      },
      onClick: (e) => {
        if (sharePressTriggeredRef.current) {
          e.preventDefault();
          e.stopPropagation();
          sharePressTriggeredRef.current = false;
          return;
        }
        if (cloneMode !== 'selected') return;
        const tag = e.target?.closest?.('button, a, input, textarea, select, audio, video, iframe, label');
        if (tag) return;
        toggleShareItem(type, sid);
      },
    };
  };

  const closeTraining = () => {
    setShowTraining(false);
    setTrainingChoreographies([]);
    setTrainingStarted(false);
    if (trainingModalRef.current?.open) {
      trainingModalRef.current.close();
    }
  };

  // Funzione per estrarre randomicamente le coreografie
  const startTraining = (e) => {
    e.preventDefault();
    let filtered = choreographies;
    if (trainingDifficulty !== 'all') {
      filtered = choreographies.filter(c => c.difficulty === trainingDifficulty);
    }
    // Deduplica: una sola occorrenza per coreografia (id).
    const uniqueMap = new Map();
    filtered.forEach((c) => {
      const key = String(c.id);
      if (!uniqueMap.has(key)) uniqueMap.set(key, c);
    });
    const distinctChoreographies = Array.from(uniqueMap.values());
    // Shuffle e prendi il numero richiesto
    const shuffled = distinctChoreographies.sort(() => 0.5 - Math.random());
    setTrainingChoreographies(shuffled.slice(0, Number(trainingCount)));
    setTrainingStarted(true);
  };

  // Funzione per avviare il training su una coreografia
  const handleTrainingPlay = (choreography) => {
    const primaryMedia = getChoreographyMediaList(choreography)[0];
    if (!primaryMedia?.url) return;
    handlePlayMedia({
      title: choreography.title,
      artist: choreography.artist,
      url: primaryMedia.url,
      mimeType: primaryMedia.mimeType || choreography.mimeType || '',
      mediaId: primaryMedia.id || null,
      sourceType: primaryMedia.sourceType || '',
      choreographyId: choreography.id,
      isChoreographyOnly: true
    });
  };

  const toggleChoreoMediaExpand = (expandKey) => {
    setExpandedChoreoMediaIds((prev) =>
      prev.includes(expandKey) ? prev.filter((x) => x !== expandKey) : [...prev, expandKey]
    );
  };

  const playOrExpandChoreoMedia = (choreography, expandKey) => {
    const mediaList = getChoreographyMediaList(choreography);
    if (!mediaList.length) return;
    if (mediaList.length === 1) {
      handleTrainingPlay(choreography);
      return;
    }
    toggleChoreoMediaExpand(expandKey);
  };

  const isLocalChoreographyUpload = (mediaOrUrl) => {
    if (!mediaOrUrl) return false;
    if (typeof mediaOrUrl === 'string') return mediaOrUrl.startsWith('/uploads/');
    const url = mediaOrUrl.url || '';
    return mediaOrUrl.sourceType === 'upload' || url.startsWith('/uploads/');
  };

  const suggestChoreographyDownloadName = (choreo, media, mediaIndex = 0) => {
    const raw = (media?.label || choreo?.title || `coreografia-${mediaIndex + 1}`).trim();
    const base = raw.replace(/[^\w\-àèéìòù]+/gi, '_').replace(/_+/g, '_').slice(0, 60) || 'coreografia';
    const url = media?.url || '';
    const extMatch = url.match(/\.[a-z0-9]{2,5}$/i);
    const ext = extMatch ? extMatch[0] : '.mp4';
    return `${base}${ext}`;
  };

  const downloadChoreographyUpload = async (choreo, media, mediaIndex = 0) => {
    if (!choreo?.id || !media?.url) return;
    if (!isLocalChoreographyUpload(media)) {
      setErrorMessage('Download disponibile solo per video caricati (non YouTube o link esterni).');
      return;
    }
    if (!authToken) {
      setErrorMessage('Devi essere loggato per scaricare il video.');
      return;
    }
    const key = `${choreo.id}-${media.id || media.url}-${mediaIndex}`;
    if (downloadingMediaKey) return;
    setDownloadingMediaKey(key);
    setErrorMessage('');
    try {
      const filename = suggestChoreographyDownloadName(choreo, media, mediaIndex);
      const mediaId = media.id != null ? String(media.id) : '';
      const useMediaRoute = mediaId && !mediaId.startsWith('legacy-');
      const endpoint = useMediaRoute
        ? `${API_BASE_URL}/api/choreographies/${choreo.id}/media/${encodeURIComponent(mediaId)}/download`
        : `${API_BASE_URL}/api/choreographies/${choreo.id}/download-upload?url=${encodeURIComponent(media.url)}`;
      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          Accept: '*/*',
        },
      });
      if (!response.ok) {
        let msg = `Download non riuscito (${response.status})`;
        try {
          const errData = await response.json();
          if (errData?.message) msg = errData.message;
        } catch (_) { /* blob/html */ }
        throw new Error(msg);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    } catch (err) {
      console.error('Download coreografia:', err);
      setErrorMessage(err.message || 'Errore durante il download del video.');
    } finally {
      setDownloadingMediaKey(null);
    }
  };

    const playChoreographyMediaItem = (choreo, media) => {
    if (!media?.url) return;
    handlePlayMedia({
      title: choreo.title,
      artist: choreo.artist,
      url: media.url,
      mimeType: media.mimeType || choreo.mimeType || '',
      mediaId: media.id || null,
      sourceType: media.sourceType || '',
      choreographyId: choreo.id,
      isChoreographyOnly: true
    });
  };

  const renderChoreographyMediaChips = (choreo, { expanded = false } = {}) => {
    const mediaList = getChoreographyMediaList(choreo);
    if (!mediaList.length) return null;
    return (
      <div className={`mt-2 flex-wrap gap-1.5 ${expanded ? 'flex' : 'hidden'} sm:flex`}>
        {mediaList.map((media, mediaIndex) => {
          const mediaItem = {
            title: choreo.title,
            artist: choreo.artist,
            url: media.url,
            mimeType: media.mimeType || choreo.mimeType || ''
          };
          const displayKind = getChoreographyMediaDisplayKind(mediaItem);
          const canDownload = isLocalChoreographyUpload(media);
          const dlKey = `${choreo.id}-${media.id || media.url}-${mediaIndex}`;
          const isDl = downloadingMediaKey === dlKey;
          return (
            <div
              key={`${choreo.id}-${media.id || media.url}-${mediaIndex}`}
              className="inline-flex items-center overflow-hidden rounded-full border border-gray-200 bg-gray-50 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
            >
              <button
                type="button"
                onClick={() => playChoreographyMediaItem(choreo, media)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1"
                title={`Riproduci ${getChoreographyMediaLabel(media, mediaIndex)}`}
              >
                {renderChoreographyMediaIcon(displayKind)}
                <span>{getChoreographyMediaLabel(media, mediaIndex)}</span>
              </button>
              {canDownload && (
                <button
                  type="button"
                  disabled={Boolean(downloadingMediaKey)}
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadChoreographyUpload(choreo, media, mediaIndex);
                  }}
                  className="border-l border-gray-200 px-2 py-1 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 dark:border-gray-600 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
                  title={isDl ? 'Download in corso…' : 'Scarica video'}
                  aria-label="Scarica video"
                >
                  {isDl ? (
                    <span className="text-[10px] font-semibold">…</span>
                  ) : (
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Funzione per gestire il login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const loginUrl = `${API_BASE_URL}/api/login`;
      console.log('Tentativo di login a:', loginUrl);
      
      const response = await fetch(loginUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      console.log('Status:', response.status);
      console.log('Headers:', Object.fromEntries(response.headers.entries()));
      
      const responseText = await response.text();
      console.log('Risposta raw:', responseText);
      console.log('Lunghezza risposta:', responseText.length);
      console.log('Primi 10 caratteri:', responseText.substring(0, 10));
      
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('Risposta parsata:', data);
      } catch (parseError) {
        console.error('Errore nel parsing della risposta:', parseError);
        console.error('Contenuto della risposta:', responseText);
        throw new Error('Risposta non valida dal server');
      }
      
      if (!response.ok) {
        throw new Error(data.message || 'Errore durante il login');
      }

      if (data.success) {
        localStorage.setItem('authToken', data.data.token);
        setAuthToken(data.data.token);
        setIsLoggedIn(true);
        setIsAuthenticated(true);
        setUser(data.data.user);
        setCurrentUsername(data.data.user.username);
        const accepted = Boolean(data.data.user?.termsAccepted);
        const lastAccepted = data.data.user?.termsAcceptedAt || null;
        setTermsAccepted(accepted);
        setTermsLastAccepted(lastAccepted);
        setShowTermsModal(!accepted);
        setMessage({ type: 'success', text: data.message });
        setUsername('');
        setPassword('');
        const pendingShare = readShareTokenFromUrl();
        if (pendingShare) setShareViewToken(pendingShare);
      } else {
        throw new Error(data.message || 'Errore durante il login');
      }
    } catch (err) {
      console.error('Errore nel login:', err);
      setMessage({ 
        type: 'error', 
        text: err.message || 'Errore di connessione al server. Verifica che il backend sia in esecuzione.'
      });
    } finally {
      setLoading(false);
    }
  };

  // Funzione per gestire la registrazione
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setRegistrationResetCode('');
    setShowRegistrationResetCode(false);
    setRegistrationMessage('');
    const datiInviati = { username, password, registrationKey, email };
    console.log('Dati inviati per la registrazione:', datiInviati); // <-- LOG AGGIUNTO
    try {
      const response = await fetch(`${API_BASE_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(datiInviati)
      });
      const responseText = await response.text();
      console.log('Risposta raw backend registrazione:', responseText); // <-- LOG RAW
      let data = {};
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.log('Errore parsing JSON:', parseError);
        data = { message: responseText };
      }
      console.log('Risposta backend registrazione (parsed):', data);
      if (!response.ok) {
        throw new Error(data.message || 'Errore durante la registrazione');
      }
      setRegistrationResetCode(data.resetCode);
      setShowRegistrationResetCode(true);
      setRegistrationMessage('Registrazione completata! Copia e conserva il codice qui sotto: ti servirà per recuperare la password.');
      setIsRegistering(false);
      setUsername('');
      setPassword('');
      setEmail('');
      setRegistrationKey('');
    } catch (err) {
      console.log('Errore registrazione:', err);
      setErrorMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Effetto per applicare la modalità scura al documento
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Funzione per gestire il logout (definita qui per essere stabile per useCallback)
  const handleLogout = useCallback(() => {
    // Rimuovi il token e l'username dal localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUsername');
    
    // Resetta tutti gli stati
    setAuthToken(null);
    setCurrentUsername('');
    setIsLoggedIn(false);
    setIsAuthenticated(false);
    setUser(null);
    setSongs([]);
    setChoreographies([]);
    setAssociations([]);
    setCloneRequestsInbox([]);
    setTermsAccepted(false);
    setTermsLastAccepted(null);
    setShowTermsModal(false);
    setErrorMessage('');
    setMessage(null);
    setUsername('');
    setPassword('');
    
    // Chiudi il menu se aperto
    setIsMenuOpen(false);
  }, []);

  // Funzione generica per effettuare chiamate API con token di autenticazione
  // Avvolta in useCallback per stabilizzare la funzione e risolvere il warning di useEffect
  const fetchData = useCallback(async (endpoint, options = {}) => {
    setErrorMessage('');
    try {
      const headers = { ...options.headers };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers,
        cache: options.cache ?? 'default',
      });

      const readErrorMessage = async () => {
        const errorText = await response.text();
        try {
          const errorData = JSON.parse(errorText);
          return errorData.message || errorData.error || 'Errore nella richiesta API';
        } catch (_) {
          return errorText || 'Errore nella richiesta API';
        }
      };

      if (response.status === 401) {
        handleLogout();
        throw new Error('Sessione scaduta. Effettua nuovamente il login.');
      }

      if (response.status === 403) {
        const message = await readErrorMessage();
        const isAuthError = /token|sessione|autorizzat|login/i.test(message);
        if (isAuthError) {
          handleLogout();
          throw new Error('Sessione non autorizzata. Effettua nuovamente il login.');
        }
        throw new Error(message);
      }

      if (!response.ok) {
        throw new Error(await readErrorMessage());
      }
      if (response.status === 204) {
        return {};
      }
      return await response.json();
    } catch (error) {
      console.error("Errore nella richiesta API:", error);
      setErrorMessage(error.message || "Si è verificato un errore di rete.");
      throw error;
    }
  }, [authToken, handleLogout, API_BASE_URL]);

  const loadMainLists = useCallback(
    async (fetchOptions = {}) => {
      if (!authToken) return;
      const applyCached = async () => {
        const cached = await loadCachedMainLists();
        setSongs(cached.songs || []);
        setChoreographies(cached.choreographies || []);
        setAssociations(cached.associations || []);
        setDataStale(true);
        return cached;
      };

      if (!isOnline()) {
        await applyCached();
        return;
      }

      try {
        const [songsData, choreoData, assocData] = await Promise.all([
          fetchData('/api/songs', fetchOptions),
          fetchData('/api/choreographies', fetchOptions),
          fetchData('/api/associations', fetchOptions),
        ]);
        setSongs(songsData);
        setChoreographies(choreoData);
        setAssociations(assocData);
        setDataStale(false);
        await saveMainLists({
          songs: songsData,
          choreographies: choreoData,
          associations: assocData,
        });
      } catch (error) {
        console.warn('Fetch liste fallito, uso cache locale:', error);
        await applyCached();
      }
    },
    [authToken, fetchData]
  );

  const requireOnline = useCallback((actionLabel = 'Questa operazione') => {
    if (isOnline()) return true;
    setErrorMessage(`${actionLabel} è disponibile solo online.`);
    return false;
  }, []);

  // Nuova funzione helper per l'upload con progress bar
  const uploadFileWithProgress = useCallback((endpoint, formData, setProgress, setUploading, method = 'POST') => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(method, `${API_BASE_URL}${endpoint}`, true); 

      if (authToken) {
        xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentCompleted = Math.round((event.loaded * 100) / event.total);
          setProgress(percentCompleted);
        }
      };

      xhr.onload = () => {
        setUploading(false);
        setProgress(0); 
        if (xhr.status >= 200 && xhr.status < 300) {
          if (xhr.status === 204) { 
            resolve({});
          } else {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              resolve({}); 
            }
          }
        } else if (xhr.status === 401) {
          handleLogout();
          reject(new Error('Sessione scaduta. Effettua nuovamente il login.'));
        } else if (xhr.status === 403) {
          let message = 'Operazione non consentita.';
          try {
            const errorData = JSON.parse(xhr.responseText);
            message = errorData.message || message;
          } catch (e) {
            message = xhr.responseText || message;
          }
          if (/token|sessione|autorizzat|login/i.test(message)) {
            handleLogout();
            reject(new Error('Sessione non autorizzata. Effettua nuovamente il login.'));
          } else {
            reject(new Error(message));
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            reject(new Error(errorData.message || 'Errore durante l\'upload del file.'));
          } catch (e) {
            reject(new Error(xhr.responseText || 'Errore sconosciuto durante l\'upload del file.'));
          }
        }
      };

      xhr.onerror = () => {
        setUploading(false);
        setProgress(0); 
        reject(new Error('Errore di rete o server non raggiungibile durante l\'upload.'));
      };

      xhr.send(formData);
    });
  }, [authToken, handleLogout, API_BASE_URL]);


  // Caricamento dati principali (login / cambio token). Su reload forza lista pulita e no cache HTTP.
  useEffect(() => {
    if (!authToken) return;
    let fetchOpts = {};
    try {
      const nav = performance.getEntriesByType?.('navigation')?.[0];
      if (nav?.type === 'reload') {
        setSearchTerm('');
        fetchOpts = { cache: 'no-store' };
      }
    } catch (_) {}
    loadMainLists(fetchOpts).catch((error) => {
      console.error('Errore nel caricamento dei dati:', error);
    });
  }, [authToken, loadMainLists]);

  // BFCache: ripristina stato vecchio (filtro + liste); azzera filtro e ricarica dal server.
  useEffect(() => {
    if (!authToken) return undefined;
    const onPageShow = (e) => {
      if (!e.persisted) return;
      setSearchTerm('');
      loadMainLists({ cache: 'no-store' }).catch((err) => {
        console.error('Errore ricaricamento dopo BFCache:', err);
      });
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [authToken, loadMainLists]);

  useEffect(() => {
    if (!isLoggedIn || !authToken) return;
    loadCloneRequestsInbox();
    const t = setInterval(() => {
      loadCloneRequestsInbox();
    }, 5000);
    const onFocus = () => loadCloneRequestsInbox();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') loadCloneRequestsInbox();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isLoggedIn, authToken, loadCloneRequestsInbox]);

  // Toast errori/successi: auto-chiusura
  useEffect(() => {
    if (!errorMessage && !(message && message.text)) return undefined;
    const t = setTimeout(() => {
      setErrorMessage('');
      setMessage(null);
    }, 4500);
    return () => clearTimeout(t);
  }, [errorMessage, message]);


  const applyTermsState = (accepted, lastAccepted, { openModalIfNeeded = false } = {}) => {
    const ok = Boolean(accepted);
    setTermsAccepted(ok);
    setTermsLastAccepted(lastAccepted || null);
    if (openModalIfNeeded && !ok) {
      setShowTermsModal(true);
    }
  };

  const loadTermsAcceptance = async () => {
    if (!isLoggedIn || !authToken) return;

    try {
      const data = await fetchData('/api/users/terms-acceptance');
      if (data.success && data.data) {
        applyTermsState(data.data.accepted, data.data.lastAccepted, { openModalIfNeeded: true });
      }
      // In caso di risposta non success: non azzerare uno stato già accettato in sessione
    } catch (error) {
      console.error('Errore caricamento termini:', error);
      // Non resettare termsAccepted su errore rete: evita di "perdere" un’accettazione appena salvata
    }
  };

  const saveTermsAcceptance = async () => {
    if (!isLoggedIn || !authToken) {
      setErrorMessage('Devi essere loggato per accettare i termini.');
      return false;
    }
    if (isSavingTerms) return false;

    setIsSavingTerms(true);
    setErrorMessage('');
    try {
      const data = await fetchData('/api/users/terms-acceptance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accepted: true }),
      });

      if (!data.success) {
        throw new Error(data.message || 'Errore nel salvataggio dei termini');
      }

      const lastAccepted = data.data?.lastAccepted || new Date().toISOString();
      applyTermsState(true, lastAccepted);
      setShowTermsModal(false);
      setErrorMessage('');
      return true;
    } catch (error) {
      console.error('Errore accettazione termini:', error);
      setErrorMessage(error.message || "Errore nell'accettazione dei termini. Riprova più tardi.");
      return false;
    } finally {
      setIsSavingTerms(false);
    }
  };

  const handleAcceptTerms = async () => {
    await saveTermsAcceptance();
  };

  const handleTermsCheckboxChange = async (e) => {
    const checked = e.target.checked;
    if (!checked) {
      // Revoca non supportata: il checkbox controllato resta sullo stato server
      return;
    }
    const ok = await saveTermsAcceptance();
    if (!ok) {
      setTermsAccepted(false);
    }
  };

    const handleSaveSong = async () => {
    if (!requireOnline('Salvataggio canzone')) return;
    setErrorMessage('');
    let saveOk = false; // chiude la modale anche se poi il refresh post-salvataggio fallisce
    const normalizedTitle = (newSongTitle || '').trim();
    const normalizedArtist = (newSongArtist || '').trim();
    const normalizedGenre = (newSongGenre || '').trim();
    const normalizedUrl = (newSongUrl || '').trim();
    
    // Verifica accettazione termini
    if (!termsAccepted) {
      if (editSongModalRef.current) {
        editSongModalRef.current.close();
      }
      setShowTermsModal(true);
      setErrorMessage("Per favore, accetta i termini e le condizioni prima di aggiungere una canzone.");
      return;
    }

    if (!normalizedTitle || !normalizedArtist || (!normalizedUrl && !newSongFile) || !normalizedGenre) {
      setErrorMessage("Per favore, compila titolo, artista, genere e fornisci un URL O carica un file per la canzone.");
      return;
    }
    if (uploadingSong) {
      setErrorMessage("Caricamento canzone in corso, attendere.");
      return;
    }

    if (newSongFile && !newSongFile.type.startsWith('audio/')) {
      setErrorMessage("Per le canzoni, si prega di caricare un file audio (es. MP3, WAV, OGG, FLAC).");
      return;
    }
    const duplicateSong = songs.find((song) => {
      const sameTitle = (song.title || '').trim().toLowerCase() === normalizedTitle.toLowerCase();
      const sameArtist = (song.artist || '').trim().toLowerCase() === normalizedArtist.toLowerCase();
      const differentEntity = !editingSong || String(song.id) !== String(editingSong.id);
      return sameTitle && sameArtist && differentEntity;
    });
    if (duplicateSong) {
      setErrorMessage("Esiste gia una canzone con lo stesso titolo e artista.");
      return;
    }

    try {
      if (newSongFile) {
        setUploadingSong(true);
        const formData = new FormData();
        formData.append('file', newSongFile);
        formData.append('title', normalizedTitle);
        formData.append('artist', normalizedArtist);
        formData.append('genre', normalizedGenre);
        formData.append('notes', newSongNotes);
        formData.append('url', normalizedUrl);
        formData.append('mimeType', newSongFile.type);

        const response = await uploadFileWithProgress(
          editingSong ? `/api/songs/${editingSong.id}` : '/api/songs',
          formData,
          setSongUploadProgress,
          setUploadingSong,
          editingSong ? 'PUT' : 'POST'
        );

        if (response && response.url && !response.url.startsWith('/uploads/')) {
          response.url = `/uploads/${response.url.split('/').pop()}`;
        }

        // La scrittura lato backend è andata a buon fine.
        saveOk = true;

        // Aggiorna subito la UI usando la risposta del backend.
        // Evita di restare "chiusa ma non aggiornata" se il refresh list fallisce.
        if (response && response.id) {
          setSongs(prev => {
            if (editingSong) {
              return prev.map(s => (String(s.id) === String(response.id) ? { ...s, ...response } : s));
            }
            // Evita duplicati se l'elemento esiste già con id numerico/string diverso.
            return prev.some(s => String(s.id) === String(response.id)) ? prev : [response, ...prev];
          });
        }
      } else {
        const formData = new FormData();
        formData.append('title', normalizedTitle);
        formData.append('artist', normalizedArtist);
        formData.append('genre', normalizedGenre);
        formData.append('notes', newSongNotes);
        formData.append('url', normalizedUrl);

        if (editingSong) {
          const savedSong = await fetchData(`/api/songs/${editingSong.id}`, {
            method: 'PUT',
            body: formData
          });

          saveOk = true;
          setSongs(prev => prev.map(s => (String(s.id) === String(savedSong.id) ? { ...s, ...savedSong } : s)));
        } else {
          const savedSong = await fetchData('/api/songs', {
            method: 'POST',
            body: formData
          });

          saveOk = true;
          setSongs(prev => prev.some(s => String(s.id) === String(savedSong.id)) ? prev : [savedSong, ...prev]);
        }
      }
    } catch (e) {
      setErrorMessage(e.message || "Errore durante il salvataggio della canzone.");
    } finally {
      setUploadingSong(false);
      setSongUploadProgress(0);

      if (saveOk) {
        setNewSongTitle('');
        setNewSongArtist('');
        setNewSongUrl('');
        setNewSongFile(null);
        setNewSongGenre('');
        setNewSongNotes('');
        setEditingSong(null);
        setErrorMessage('');

        if (editSongModalRef.current) {
          editSongModalRef.current.close();
        }
      }
    }
  };

  const handleEditSong = (song) => {
    if (!requireOnline('Modifica canzone')) return;
    setEditingSong(song);
    setNewSongTitle(song.title);
    setNewSongArtist(song.artist);
    setNewSongUrl(song.url || '');
    setNewSongFile(null);
    setNewSongGenre(song.genre || '');
    setNewSongNotes(song.notes || ''); 
    setErrorMessage('');
    setSongUploadProgress(0); 
    setUploadingSong(false); 
    if (editSongModalRef.current) {
      editSongModalRef.current.showModal();
    }
  };

  const handleCancelEditSong = () => {
    setEditingSong(null);
    setNewSongTitle('');
    setNewSongArtist('');
    setNewSongUrl('');
    setNewSongFile(null);
    setNewSongGenre('');
    setNewSongNotes(''); 
    setErrorMessage('');
    setSongUploadProgress(0); 
    setUploadingSong(false); 
    if (editSongModalRef.current) {
      editSongModalRef.current.close();
    }
  };

  const isLikelyChoreographyVideoFile = (file) => {
    if (!file) return false;
    const t = (file.type || '').toLowerCase();
    if (t.startsWith('video/')) return true;
    // Alcuni browser/OS lasciano type vuoto o mettono application/octet-stream per .mp4
    if (t === 'application/octet-stream' || t === '') {
      const n = (file.name || '').toLowerCase();
      return /\.(mp4|webm|ogg|mov|m4v|mkv)$/.test(n);
    }
    return false;
  };

  const createEmptyChoreographyMediaItem = () => ({
    clientId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    id: null,
    url: '',
    file: null,
    label: '',
    originalUrl: '',
    originalLabel: '',
    originalSortOrder: null
  });

  const getChoreographyMediaList = (choreo) => {
    if (Array.isArray(choreo?.media) && choreo.media.length > 0) return choreo.media;
    if (choreo?.url) {
      return [{
        id: null,
        url: choreo.url,
        mimeType: choreo.mimeType || '',
        label: '',
        sortOrder: 0
      }];
    }
    return [];
  };


  const pickChoreographyThumbMedia = (choreo) => {
    const list = getChoreographyMediaList(choreo);
    if (!list.length) return null;
    const ranked = [...list].sort((a, b) => {
      const score = (m) => {
        const u = m?.url || '';
        if (getYouTubeId(u)) return 0;
        const t = getMediaType({ url: u, mimeType: m?.mimeType || '' });
        if (t === 'video') return 1;
        if ((u || '').startsWith('/uploads/')) return 2;
        return 3;
      };
      return score(a) - score(b);
    });
    return ranked[0] || null;
  };

  const renderChoreographyThumb = (choreo, onPlay) => {
    const first = pickChoreographyThumbMedia(choreo);
    if (!first?.url) return null;
    const mediaItem = {
      title: choreo.title,
      artist: choreo.artist,
      url: first.url,
      mimeType: first.mimeType || choreo.mimeType || ''
    };
    const ytId = getYouTubeId(first.url);
    const kind = getMediaType(mediaItem);
    const play = (e) => {
      e.stopPropagation();
      onPlay(mediaItem);
    };
    if (ytId) {
      return (
        <button type="button" className="tile-thumb" title="Apri video" onClick={play}>
          <img src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} alt="" loading="lazy" />
          <span className="tile-thumb-badge" aria-hidden>
            <svg fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
          </span>
        </button>
      );
    }
    if (kind === 'video' || (first.url || '').startsWith('/uploads/')) {
      return (
        <button type="button" className="tile-thumb" title="Apri video" onClick={play}>
          <video muted preload="metadata" src={getStreamingUrl(first.url)} />
          <span className="tile-thumb-badge" aria-hidden>
            <svg fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
          </span>
        </button>
      );
    }
    return (
      <button type="button" className="tile-thumb-link" title="Apri media" onClick={play}>
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
        Link
      </button>
    );
  };

  const getChoreographyMediaLabel = (media, mediaIndex) => {
    const label = (media?.label || '').trim();
    return label || `Media ${mediaIndex + 1}`;
  };

  const getChoreographyMediaDisplayKind = (mediaItem) => {
    const mediaType = getMediaType(mediaItem);
    if (mediaType === 'youtube') return 'youtube';
    if (mediaType === 'video' || (mediaItem?.url || '').startsWith('/uploads/')) return 'upload';
    return 'external';
  };

  const renderChoreographyMediaIcon = (kind) => {
    if (kind === 'youtube') {
      return (
        <svg className="h-5 w-5 shrink-0 text-red-600 dark:text-red-400" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 00.5 6.2 31.6 31.6 0 000 12a31.6 31.6 0 00.5 5.8 3 3 0 002.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 002.1-2.1A31.6 31.6 0 0024 12a31.6 31.6 0 00-.5-5.8zM9.75 15.02V8.98L15.5 12l-5.75 3.02z" />
        </svg>
      );
    }
    if (kind === 'upload') {
      return (
        <svg className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      );
    }
    return (
      <svg className="h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
      </svg>
    );
  };

  const resetChoreographyForm = () => {
    setNewChoreographyTitle('');
    setNewChoreographyArtist('');
    setNewChoreographyDifficulty('');
    setNewChoreographyNotes('');
    setEditingChoreography(null);
    setChoreographyMediaItems([createEmptyChoreographyMediaItem()]);
    setRemovedChoreographyMediaIds([]);
    setChoreoUploadProgress(0);
    setUploadingChoreography(false);
  };

  const openNewChoreographyModal = () => {
    if (!requireOnline('Creazione coreografia')) return;
    resetChoreographyForm();
    setErrorMessage('');
    if (editChoreographyModalRef.current) {
      editChoreographyModalRef.current.showModal();
    }
  };

  const handleSaveChoreography = async () => {
    if (!requireOnline('Salvataggio coreografia')) return;
    setErrorMessage('');
    let saveOk = false;
    const normalizedTitle = (newChoreographyTitle || '').trim();
    const normalizedArtist = (newChoreographyArtist || '').trim();
    const normalizedDifficulty = (newChoreographyDifficulty || '').trim();

    if (!termsAccepted) {
      if (editChoreographyModalRef.current) {
        editChoreographyModalRef.current.close();
      }
      setShowTermsModal(true);
      setErrorMessage("Per favore, accetta i termini e le condizioni prima di aggiungere una coreografia.");
      return;
    }

    if (!normalizedTitle || !normalizedArtist || !normalizedDifficulty) {
      setErrorMessage("Per favore, compila titolo, artista e difficoltà.");
      return;
    }
    if (uploadingChoreography) {
      setErrorMessage("Caricamento coreografia in corso, attendere.");
      return;
    }

    const activeMediaItems = choreographyMediaItems.filter((item) => {
      const hasUrl = Boolean((item.url || '').trim());
      const hasFile = Boolean(item.file);
      return hasUrl || hasFile;
    });
    if (activeMediaItems.length === 0) {
      setErrorMessage("Aggiungi almeno un link o un file video per la coreografia.");
      return;
    }

    for (const item of activeMediaItems) {
      if (item.file && !isLikelyChoreographyVideoFile(item.file)) {
        setErrorMessage("Per le coreografie, si prega di caricare file video (es. MP4, WebM, OGG, MOV).");
        return;
      }
      if (!item.file && !(item.url || '').trim()) {
        setErrorMessage("Ogni media deve avere un link oppure un file video.");
        return;
      }
    }

    const duplicateChoreography = choreographies.find((choreo) => {
      const sameTitle = (choreo.title || '').trim().toLowerCase() === normalizedTitle.toLowerCase();
      const sameArtist = (choreo.artist || '').trim().toLowerCase() === normalizedArtist.toLowerCase();
      const differentEntity = !editingChoreography || String(choreo.id) !== String(editingChoreography.id);
      return sameTitle && sameArtist && differentEntity;
    });
    if (duplicateChoreography) {
      setErrorMessage("Esiste gia una coreografia con lo stesso titolo e artista.");
      return;
    }

    try {
      setUploadingChoreography(true);
      const metadataForm = new FormData();
      metadataForm.append('title', normalizedTitle);
      metadataForm.append('artist', normalizedArtist);
      metadataForm.append('difficulty', normalizedDifficulty);
      metadataForm.append('notes', newChoreographyNotes);

      let choreographyId = editingChoreography ? String(editingChoreography.id) : null;
      if (editingChoreography) {
        await fetchData(`/api/choreographies/${choreographyId}`, {
          method: 'PUT',
          body: metadataForm
        });
      } else {
        const created = await fetchData('/api/choreographies', {
          method: 'POST',
          body: metadataForm
        });
        choreographyId = String(created.id);
      }

      const mediaIdsToDelete = new Set(
        removedChoreographyMediaIds.filter((mediaId) => mediaId && !String(mediaId).startsWith('legacy-'))
      );
      const keptExistingIds = new Set(
        activeMediaItems.map((item) => item.id).filter((mediaId) => mediaId && !String(mediaId).startsWith('legacy-'))
      );
      choreographyMediaItems.forEach((item) => {
        if (item.id && !keptExistingIds.has(item.id) && !String(item.id).startsWith('legacy-')) {
          mediaIdsToDelete.add(item.id);
        }
      });

      const mediaToPost = [];
      const mediaToPut = [];

      activeMediaItems.forEach((item, index) => {
        const normalizedUrl = (item.url || '').trim();
        const originalUrl = (item.originalUrl || '').trim();
        const isExisting = Boolean(item.id) && !String(item.id).startsWith('legacy-');

        if (item.file) {
          if (isExisting) mediaIdsToDelete.add(item.id);
          mediaToPost.push({ item, index });
          return;
        }

        if (!normalizedUrl) return;

        if (!isExisting) {
          mediaToPost.push({ item, index });
          return;
        }

        if (normalizedUrl !== originalUrl) {
          const wasUpload = originalUrl.startsWith('/uploads/');
          const willBeUpload = normalizedUrl.startsWith('/uploads/');
          if (!item.file && !wasUpload && !willBeUpload) {
            mediaToPut.push({ item, index });
          } else {
            mediaIdsToDelete.add(item.id);
            mediaToPost.push({ item, index });
          }
          return;
        }

        const labelChanged = (item.label || '') !== (item.originalLabel || '');
        const orderChanged = item.originalSortOrder !== null && item.originalSortOrder !== index;
        if (labelChanged || orderChanged) {
          mediaToPut.push({ item, index });
        }
      });

      for (const mediaId of mediaIdsToDelete) {
        await fetchData(`/api/choreographies/${choreographyId}/media/${mediaId}`, {
          method: 'DELETE'
        });
      }

      for (const { item, index } of mediaToPut) {
        await fetchData(`/api/choreographies/${choreographyId}/media/${item.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            url: (item.url || '').trim(),
            label: item.label || '',
            sortOrder: index
          })
        });
      }

      for (const { item, index } of mediaToPost) {
        const normalizedUrl = (item.url || '').trim();
        const mediaForm = new FormData();
        if (item.label) mediaForm.append('label', item.label);
        mediaForm.append('sortOrder', String(index));
        if (item.file) {
          mediaForm.append('file', item.file);
          mediaForm.append('mimeType', item.file.type);
        } else if (normalizedUrl) {
          mediaForm.append('url', normalizedUrl);
        }

        await uploadFileWithProgress(
          `/api/choreographies/${choreographyId}/media`,
          mediaForm,
          setChoreoUploadProgress,
          setUploadingChoreography,
          'POST'
        );
      }

      const refreshed = await fetchData('/api/choreographies');
      setChoreographies(Array.isArray(refreshed) ? refreshed : []);
      saveOk = true;
    } catch (e) {
      setErrorMessage(e.message || "Errore durante il salvataggio della coreografia.");
    } finally {
      setUploadingChoreography(false);
      setChoreoUploadProgress(0);

      if (saveOk) {
        resetChoreographyForm();
        setErrorMessage('');
        if (editChoreographyModalRef.current) {
          editChoreographyModalRef.current.close();
        }
      }
    }
  };

  const handleEditChoreography = (choreo) => {
    if (!requireOnline('Modifica coreografia')) return;
    setEditingChoreography(choreo);
    setNewChoreographyTitle(choreo.title);
    setNewChoreographyArtist(choreo.artist);
    setNewChoreographyDifficulty(choreo.difficulty || '');
    setNewChoreographyNotes(choreo.notes || '');
    const media = getChoreographyMediaList(choreo);
    setChoreographyMediaItems(
      media.length
        ? media.map((m, index) => ({
            clientId: `${m.id || 'new'}-${Math.random().toString(36).slice(2, 8)}`,
            id: m.id || null,
            url: m.url || '',
            file: null,
            label: m.label || '',
            originalUrl: m.url || '',
            originalLabel: m.label || '',
            originalSortOrder: m.sortOrder ?? index
          }))
        : [createEmptyChoreographyMediaItem()]
    );
    setRemovedChoreographyMediaIds([]);
    setErrorMessage('');
    setChoreoUploadProgress(0);
    setUploadingChoreography(false);
    if (editChoreographyModalRef.current) {
      editChoreographyModalRef.current.showModal();
    }
  };

  const handleCancelEditChoreography = () => {
    resetChoreographyForm();
    setErrorMessage('');
    if (editChoreographyModalRef.current) {
      editChoreographyModalRef.current.close();
    }
  };

  const resetNewAssociationForm = () => {
    setSelectedChoreographyId('');
    setSelectedSongIds([]);
    setNewAssociationNotes('');
    setErrorMessage('');
  };

  const openNewAssociationModal = () => {
    if (!requireOnline('Creazione associazione')) return;
    resetNewAssociationForm();
    if (newAssociationModalRef.current) {
      newAssociationModalRef.current.showModal();
    }
  };

  const handleCancelNewAssociation = () => {
    resetNewAssociationForm();
    if (newAssociationModalRef.current) {
      newAssociationModalRef.current.close();
    }
  };

  const handleEditAssociation = (association) => {
    if (!requireOnline('Modifica associazione')) return;
    setEditingAssociation({
      ...association,
      songIds: [association.songId] // Convertiamo il singolo songId in un array
    });
    if (editAssociationModalRef.current) {
      editAssociationModalRef.current.showModal();
    }
  };

  const handleCancelEditAssociation = () => {
    setEditingAssociation(null);
    setSelectedChoreographyId('');
    setSelectedSongIds([]);
    setNewAssociationNotes('');
    setErrorMessage('');
    if (editAssociationModalRef.current) {
      editAssociationModalRef.current.close();
    }
  };

  const handleSaveAssociation = async () => {
    if (!requireOnline('Salvataggio associazione')) return;
    try {
      if (!editingAssociation?.choreographyId || !editingAssociation?.songIds?.length) {
        setErrorMessage('Seleziona almeno una canzone e una coreografia');
        return;
      }

      // Prima eliminiamo l'associazione esistente
      await fetchData(`/api/associations/${editingAssociation.id}`, {
        method: 'DELETE'
      });

      // Poi creiamo le nuove associazioni per ogni canzone selezionata
      for (const songId of editingAssociation.songIds) {
        await fetchData('/api/associations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            songId,
            choreographyId: editingAssociation.choreographyId,
            notes: editingAssociation.notes
          })
        });
      }
      
      // Ricarica i dati
      const updatedAssociations = await fetchData('/api/associations');
      setAssociations(updatedAssociations);
      
      // Chiudi il modal
      if (editAssociationModalRef.current) {
        editAssociationModalRef.current.close();
      }
      
      setEditingAssociation(null);
      setErrorMessage('');
    } catch (error) {
      console.error('Errore durante il salvataggio dell\'associazione:', error);
      setErrorMessage('Errore durante il salvataggio dell\'associazione');
    }
  };

  const handleDeleteAssociation = async (id) => {
    try {
      await fetchData(`/api/associations/${id}`, {
        method: 'DELETE'
      });
      setAssociations(associations.filter(assoc => assoc.id !== id));
    } catch (error) {
      console.error('Errore durante l\'eliminazione dell\'associazione:', error);
      setErrorMessage('Errore durante l\'eliminazione dell\'associazione');
    }
  };

  const confirmDelete = (type, id) => {
    if (!requireOnline('Eliminazione')) return;
    setItemToDelete({ type, id });
    if (confirmModalRef.current) {
      confirmModalRef.current.showModal();
    }
  };

  const openNewSongModal = useCallback(() => {
    if (!requireOnline('Creazione canzone')) return;
    setNewSongTitle('');
    setNewSongArtist('');
    setNewSongUrl('');
    setNewSongFile(null);
    setNewSongGenre('');
    setNewSongNotes('');
    setEditingSong(null);
    if (editSongModalRef.current) editSongModalRef.current.showModal();
  }, [requireOnline]);

  // Shortcut PWA: /new-song e /new-choreography
  useEffect(() => {
    if (!isLoggedIn) return;
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path === '/new-song') {
      setMainAppTab('songs');
      openNewSongModal();
      window.history.replaceState({}, '', '/');
    } else if (path === '/new-choreography') {
      setMainAppTab('choreographies');
      openNewChoreographyModal();
      window.history.replaceState({}, '', '/');
    }
  }, [isLoggedIn, openNewSongModal]);


  const getSongDetails = (songId) => {
    const song = songs.find(s => s.id === songId);
    return song ? `${song.title} - ${song.artist} (${song.genre || 'N/D'})` : 'Canzone Sconosciuta';
  };

  const getChoreographyDetails = (choreographyId) => {
    const choreography = choreographies.find(c => c.id === choreographyId);
    return choreography ? `${choreography.title} - ${choreography.artist} (Difficoltà: ${choreography.difficulty || 'N/D'})` : 'Coreografia Sconosciuta';
  };

  const getYouTubeId = (url) => {
    if (!url || typeof url !== 'string') return null;
    const regExp = /(?:https?:\/\/)?(?:www\.)?(?:m\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=|embed\/|v\/|)([\w-]{11})(?:\S+)?/;
    const match = url.match(regExp);
    return (match && match[1].length === 11) ? match[1] : null;
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = getYouTubeId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}?origin=${window.location.origin}&enablejsapi=1` : null;
  };

  const getMediaType = (item) => {
    console.log('getMediaType input:', item);
    if (!item || !item.url) return 'unknown';

    // Se è un URL di YouTube, è un video
    if (getYouTubeId(item.url)) {
      return 'youtube';
    }

    // Se è un URL esterno (non YouTube), è unknown
    if (item.url.startsWith('http')) {
      return 'unknown';
    }

    // Se abbiamo il MIME type, usiamo quello
    if (item.mimeType) {
      console.log('Using mimeType:', item.mimeType);
      if (item.mimeType.startsWith('audio/')) return 'audio';
      if (item.mimeType.startsWith('video/')) return 'video';
    }

    // Se non abbiamo il MIME type, controlliamo l'estensione
    const lowerCaseUrl = item.url.toLowerCase();
    
    // Estensioni audio
    const audioExtensions = ['.mp3', '.wav', '.ogg', '.flac', '.m4a', '.aac', '.wma'];
    if (audioExtensions.some(ext => lowerCaseUrl.endsWith(ext))) {
      return 'audio';
    }
    
    // Estensioni video
    const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
    if (videoExtensions.some(ext => lowerCaseUrl.endsWith(ext))) {
      return 'video';
    }

    // Se non riusciamo a determinare il tipo, assumiamo che sia audio per i file locali
    if (item.url.startsWith('/uploads/')) {
      return 'audio';
    }

    return 'unknown';
  };

  const getStreamingUrl = (url) => {
    console.log('getStreamingUrl input:', url);
    if (!url) return null;
    
    // Se è un URL di YouTube, restituiamo l'URL di embed
    if (getYouTubeId(url)) {
      return getYouTubeEmbedUrl(url);
    }
    
    // Se l'URL è già un URL interno del backend, lo passiamo attraverso l'endpoint di streaming
    if (url.startsWith('/uploads/')) {
      const baseUrl = API_BASE_URL.replace('/api', '');
      return `${baseUrl}${url}`;
    }
    
    // Se è un URL esterno (non YouTube), lo restituiamo direttamente
    if (url.startsWith('http')) {
      return url;
    }
    
    // Per tutti gli altri URL, li passiamo attraverso l'endpoint di streaming
    return `${API_BASE_URL}/stream?url=${encodeURIComponent(url)}`;
  };

  const handlePlayMedia = (mediaData) => {
    console.log('handlePlayMedia input:', mediaData);

    if (mediaData?.isChoreographyOnly) {
      if (!mediaData.url) {
        console.error('Dati coreografia non validi:', mediaData);
        return;
      }
      const mediaType = getMediaType(mediaData);
      if (mediaType === 'unknown' && mediaData.url.startsWith('http')) {
        window.open(mediaData.url, '_blank');
        return;
      }
      const streamUrl = getStreamingUrl(mediaData.url);
      if (!streamUrl) {
        console.error('URL di streaming non valido');
        return;
      }
      setPlayingMedia({
        title: mediaData.title,
        artist: mediaData.artist,
        type: mediaType,
        url: streamUrl,
        sourceUrl: mediaData.url || '',
        mediaId: mediaData.mediaId || null,
        sourceType: mediaData.sourceType || '',
        choreographyId: mediaData.choreographyId || null,
        canDownload: isLocalChoreographyUpload(mediaData),
        choreographyOnly: true,
        mimeType: mediaData.mimeType || ''
      });
      if (playMediaModalRef.current) {
        playMediaModalRef.current.showModal();
      }
      return;
    }

    let songData = null;
    let choreographyData = null;

    if (mediaData.song && mediaData.choreography) {
      songData = mediaData.song;
      choreographyData = mediaData.choreography;
    } else if (mediaData.item) {
      songData = mediaData.item;
    } else {
      songData = mediaData;
    }

    if (!songData || !songData.url) {
      console.error('Dati media non validi:', mediaData);
      return;
    }

    const songMediaType = getMediaType(songData);
    const choreographyMediaType = choreographyData ? getMediaType(choreographyData) : null;

    if (songMediaType === 'unknown' && songData.url.startsWith('http')) {
      window.open(songData.url, '_blank');
      return;
    }

    const songStreamingUrl = getStreamingUrl(songData.url);
    const choreographyStreamingUrl = choreographyData ? getStreamingUrl(choreographyData.url) : null;

    if (!songStreamingUrl) {
      console.error('URL di streaming non valido');
      return;
    }

    setPlayingMedia({
      title: songData.title,
      artist: songData.artist,
      type: songMediaType,
      url: songStreamingUrl,
      choreography: choreographyData ? {
        title: choreographyData.title,
        artist: choreographyData.artist,
        type: choreographyMediaType,
        url: choreographyStreamingUrl
      } : null
    });

    if (playMediaModalRef.current) {
      playMediaModalRef.current.showModal();
    }
  };

  const handleClosePlayMedia = () => {
    setPlayingMedia(null);
    if (playMediaModalRef.current) {
      playMediaModalRef.current.close();
    }
  };

  // Logica di ricerca per canzoni e coreografie
  const filterItems = (items, term) => {
    if (!term) return items;
    const lowerCaseTerm = term.toLowerCase();
    return items.filter(item => 
      (item.title && item.title.toLowerCase().includes(lowerCaseTerm)) ||
      (item.artist && item.artist.toLowerCase().includes(lowerCaseTerm)) ||
      (item.genre && item.genre.toLowerCase().includes(lowerCaseTerm)) || 
      (item.difficulty && item.difficulty.toLowerCase().includes(lowerCaseTerm)) || 
      (item.notes && item.notes.toLowerCase().includes(lowerCaseTerm))
    );
  };

  const filteredSongs = filterItems(songs, searchTerm);
  const filteredChoreographies = filterItems(choreographies, searchTerm);

  // Funzione per aggiungere associazioni multiple (o singole)
  const addAssociations = async () => {
    if (!requireOnline('Creazione associazione')) return;
    setErrorMessage('');
    
    // Verifica accettazione termini
    if (!termsAccepted) {
      setShowTermsModal(true);
      setErrorMessage("Per favore, accetta i termini e le condizioni prima di creare un'associazione.");
      return;
    }

    console.log('Inizio addAssociations');
    console.log('selectedChoreographyId:', selectedChoreographyId);
    console.log('selectedSongIds:', selectedSongIds);
    
    if (!selectedChoreographyId) {
      setErrorMessage("Per favore, seleziona una coreografia per creare un'associazione.");
      return;
    }
    if (selectedSongIds.length === 0) {
      setErrorMessage("Per favore, seleziona almeno una canzone per creare un'associazione.");
      return;
    }

    try {
      const successfulAssociations = [];
      const failedAssociations = [];

      for (const songId of selectedSongIds) {
        console.log('Processando songId:', songId);
        const existingAssociation = associations.find(
          (assoc) => assoc.songId === songId && assoc.choreographyId === selectedChoreographyId
        );

        if (existingAssociation) {
          console.log('Associazione esistente trovata per:', songId);
          failedAssociations.push(`Associazione Canzone ${songId} con Coreografia ${selectedChoreographyId} esiste già.`);
          continue;
        }

        try {
          console.log('Tentativo di creazione associazione per:', songId);
          const response = await fetchData('/api/associations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              songId: songId,
              choreographyId: selectedChoreographyId,
              notes: newAssociationNotes,
            })
          });
          console.log('Risposta creazione associazione:', response);
          successfulAssociations.push(`Canzone ${getSongDetails(songId).split(' - ')[0]}`);
        } catch (e) {
          console.error('Errore nella creazione dell\'associazione:', e);
          failedAssociations.push(`Associazione Canzone ${songId} con Coreografia ${selectedChoreographyId} fallita: ${e.message}`);
        }
      }

      if (successfulAssociations.length > 0) {
        console.log(`Associazioni aggiunte con successo per: ${successfulAssociations.join(', ')}`);
        const updatedAssociations = await fetchData('/api/associations');
        setAssociations(updatedAssociations);

        setSelectedSongIds([]);
        setSelectedChoreographyId('');
        setNewAssociationNotes('');
        setErrorMessage('');

        if (newAssociationModalRef.current) {
          newAssociationModalRef.current.close();
        }

        if (failedAssociations.length > 0) {
          setErrorMessage(`Alcune associazioni non sono state create: ${failedAssociations.join('; ')}`);
        }
      } else if (failedAssociations.length > 0) {
        setErrorMessage(`Tutte le associazioni hanno fallito o esistevano già. Dettagli: ${failedAssociations.join('; ')}`);
      }

    } catch (e) {
      console.error('Errore generale in addAssociations:', e);
      setErrorMessage(e.message || "Errore nella gestione delle associazioni multiple.");
    }
  };

  const deleteItem = async (type, id) => {
    try {
      if (type === 'song') {
        await fetchData(`/api/songs/${id}`, {
          method: 'DELETE'
        });
        setSongs(songs.filter(song => song.id !== id));
      } else if (type === 'choreography') {
        await fetchData(`/api/choreographies/${id}`, {
          method: 'DELETE'
        });
        setChoreographies(choreographies.filter(choreo => choreo.id !== id));
      } else if (type === 'association') {
        await fetchData(`/api/associations/${id}`, {
          method: 'DELETE'
        });
        setAssociations(associations.filter(assoc => assoc.id !== id));
      }
      setItemToDelete(null);
      if (confirmModalRef.current) {
        confirmModalRef.current.close();
      }
    } catch (error) {
      console.error('Errore nella richiesta API:', error);
      setErrorMessage('Errore durante l\'eliminazione. Riprova più tardi.');
    }
  };

  // Carica lo stato delle condizioni dopo login / token
  useEffect(() => {
    if (!isLoggedIn || !authToken) return;
    loadTermsAcceptance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, authToken, API_BASE_URL]);

    // Aggiungi questo useEffect per gestire i messaggi
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Verifica il token all'avvio
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/verify-token`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        
        if (response.ok && data.success) {
          console.log('Token valido, aggiorno stato');
          setIsLoggedIn(true);
          setIsAuthenticated(true);
          setUser(data.user);
          setCurrentUsername(data.user.username);
          setAuthToken(token);
          const accepted = Boolean(data.user?.termsAccepted);
          const lastAccepted = data.user?.termsAcceptedAt || null;
          setTermsAccepted(accepted);
          setTermsLastAccepted(lastAccepted);
          if (!accepted) setShowTermsModal(true);
          
          // Carica i dati solo se il token è valido
          try {
            console.log('Carico i dati...');
            const [songs, choreographies, associations] = await Promise.all([
              fetchData('/api/songs'),
              fetchData('/api/choreographies'),
              fetchData('/api/associations')
            ]);
            console.log('Dati caricati con successo');
            setSongs(songs);
            setChoreographies(choreographies);
            setAssociations(associations);
          } catch (error) {
            console.error('Errore nel caricamento dei dati:', error);
          }
        } else {
          console.log('Token non valido, logout');
          handleLogout();
        }
      } catch (error) {
        console.error('Errore nella verifica del token:', error);
        handleLogout();
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, [API_BASE_URL, handleLogout, fetchData]);

  // Aggiungi questa funzione
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    try {
      // Verifica username, email e codice di recupero
      const response = await fetch(`${API_BASE_URL}/api/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, code: resetCode })
      });
      const responseText = await response.text();
      let data = {};
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        data = { message: responseText };
      }
      if (!response.ok) {
        throw new Error(data.message || 'Errore nella verifica dei dati per il reset');
      }
      // Se ok, procedi con il reset password (mostra i campi per la nuova password o chiama direttamente /api/reset-password)
      setShowResetCode(true); // oppure mostra i campi per la nuova password
      setErrorMessage('Dati validi, puoi procedere con il reset della password.');
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Vista pubblica link di condivisione (anche senza login)
  if (shareViewToken) {
    return (
      <ShareLanding
        token={shareViewToken}
        isLoggedIn={isLoggedIn}
        autoImport={pendingShareImportAfterLogin && isLoggedIn}
        onClose={() => {
          setPendingShareImportAfterLogin(false);
          closeShareView();
        }}
        onRequestLogin={() => {
          // URL ?share= resta: dopo login/registrazione si torna qui e si importa
          setPendingShareImportAfterLogin(true);
          setShareViewToken('');
        }}
        onImported={async () => {
          setPendingShareImportAfterLogin(false);
          try {
            await loadMainLists({ cache: 'no-store' });
          } catch (_) {}
          setMessage({ type: 'success', text: 'Contenuti importati nella tua libreria.' });
          closeShareView();
        }}
      />
    );
  }

  // Renderizza il form di login/registrazione se non loggato, altrimenti l'app principale
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>;
  }

  if (!isLoggedIn) {
    // Mostra il pop-up del codice di recupero se necessario
    if (showRegistrationResetCode) {
      return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md w-96">
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900 rounded-lg border border-yellow-200 dark:border-yellow-800 mt-4 text-center">
              <p className="text-yellow-800 dark:text-yellow-200 font-semibold mb-2">
                Registrazione completata! Copia e conserva il codice qui sotto: ti servirà per recuperare la password.
              </p>
              <div className="text-2xl font-bold text-yellow-900 dark:text-yellow-100 text-center select-all mb-2">{registrationResetCode}</div>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-4">Conserva questo codice in un luogo sicuro! Senza di esso non potrai recuperare la password.</p>
              <button
                type="button"
                className="w-full bg-blue-500 text-white py-2.5 px-4 rounded-xl hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 ease-in-out shadow-sm"
                onClick={() => {
                  setShowRegistrationResetCode(false);
                  setIsRegistering(false);
                }}
              >
                Ho copiato il codice
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md w-96">
          <h2 className="text-2xl font-bold mb-6 text-center text-gray-800 dark:text-white">
            {isForgotPassword ? 'Reset Password' : (isRegistering ? 'Registrazione' : 'Login')}
          </h2>
          
          {(errorMessage || (message && message.text)) && (
            <div className={`mb-4 p-3 rounded font-semibold text-center border ${errorMessage || (message && message.type === 'error') ? 'bg-red-100 text-red-700 border-red-300' : 'bg-green-100 text-green-700 border-green-300'}`}>
              {errorMessage || (message && message.text)}
            </div>
          )}

          <form onSubmit={isForgotPassword ? handleForgotPassword : (isRegistering ? handleRegister : handleLogin)} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Username
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900 dark:text-white"
                placeholder="Inserisci username"
                required
              />
            </div>
            {!isForgotPassword && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="Inserisci password"
                  required
                />
              </div>
            )}
            {isRegistering && (
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="Inserisci la tua email"
                  required
                  autoComplete="off"
                />
              </div>
            )}
            {isRegistering && (
              <div>
                <label htmlFor="registrationKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Chiave di registrazione</label>
                <input
                  type="text"
                  id="registrationKey"
                  value={registrationKey}
                  onChange={(e) => setRegistrationKey(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900 dark:text-white"
                  placeholder="Inserisci la chiave fornita dall'amministratore"
                  required
                  autoComplete="off"
                />
              </div>
            )}

            {isForgotPassword && (
              <>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900 dark:text-white"
                    placeholder="Inserisci l'email usata in registrazione"
                    required
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label htmlFor="resetCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Codice di recupero</label>
                  <input
                    type="text"
                    id="resetCode"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900 dark:text-white"
                    placeholder="Inserisci il codice ricevuto in fase di registrazione"
                    required
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nuova Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900 dark:text-white"
                    placeholder="Inserisci la nuova password"
                    required
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Conferma Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm text-gray-900 dark:text-white"
                    placeholder="Conferma la nuova password"
                    required
                    autoComplete="off"
                  />
                </div>
              </>
            )}

            <div className="flex justify-between items-center">
              <button
                type="submit"
                className="w-full bg-blue-500 text-white py-2.5 px-4 rounded-xl hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 ease-in-out shadow-sm"
                disabled={loading}
              >
                {(() => {
                  if (loading) return 'Caricamento...';
                  if (isForgotPassword && showResetCode && hasSeenCode) return 'Reimposta Password';
                  if (isForgotPassword) return 'Verifica Email e Richiedi Reset';
                  if (isRegistering) return 'Registrati';
                  return 'Accedi';
                })()}
              </button>
            </div>
          </form>

          <div className="mt-4 text-center space-y-2">
            {!isForgotPassword && (
              <button
                onClick={() => setIsRegistering(!isRegistering)}
                className="w-full bg-blue-500 text-white py-2.5 px-4 rounded-xl hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 ease-in-out shadow-sm"
              >
                {isRegistering ? 'Hai già un account? Accedi' : 'Non hai un account? Registrati'}
              </button>
            )}
            {!isRegistering && !isForgotPassword && (
              <button
                onClick={() => setIsForgotPassword(true)}
                className="w-full bg-blue-500 text-white py-2.5 px-4 rounded-xl hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 ease-in-out shadow-sm"
              >
                Ho dimenticato la password
              </button>
            )}
            {isForgotPassword && (
              <button
                onClick={() => {
                  setIsForgotPassword(false);
                  setResetCode('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="w-full bg-blue-500 text-white py-2.5 px-4 rounded-xl hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 ease-in-out shadow-sm"
              >
                Torna al login
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Se l'utente è loggato, renderizza l'applicazione principale
  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 p-2 sm:p-4 font-sans text-gray-800 dark:text-gray-200">
      <PwaBanners
        updateRegistration={swUpdateRegistration}
        onDismissUpdate={() => setSwUpdateRegistration(null)}
      />
      <NetworkBanner online={isAppOnline} stale={dataStale} />
      {/* Header con barra di ricerca e pulsanti */}
      <div className="app-topbar sticky top-0 z-50 bg-gray-100 dark:bg-gray-900 pb-2 sm:pb-4">
        <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
          <div className="flex items-center min-w-0 gap-2">
            <img
              src="/logo-boot-note.png"
              alt=""
              width={44}
              height={44}
              className="h-9 w-9 sm:h-11 sm:w-11 shrink-0 object-contain"
              aria-hidden
            />
            <h1 className="section-title section-title--brand truncate sm:!text-3xl">
              <span className="sm:hidden">Country Hub</span>
              <span className="hidden sm:inline">Country Dance Hub</span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsDarkMode((prev) => !prev)}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-300 bg-white text-gray-700 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
              title={isDarkMode ? 'Passa a tema chiaro' : 'Passa a tema scuro'}
              aria-label={isDarkMode ? 'Passa a tema chiaro' : 'Passa a tema scuro'}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                {isDarkMode ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                )}
              </svg>
            </button>
            {isLoggedIn && (
              <button
                type="button"
                onClick={() => setIsMenuOpen((open) => !open)}
                className="relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-300 bg-white text-gray-700 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                aria-label={isMenuOpen ? 'Chiudi menu' : 'Apri menu'}
                aria-expanded={isMenuOpen}
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
                {cloneRequestsInbox.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-600 text-white text-[9px] leading-[16px] text-center font-bold">
                    {cloneRequestsInbox.length}
                  </span>
                )}
              </button>
            )}
            {isLoggedIn && (
              <>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-300 bg-white text-gray-700 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-100 sm:hidden"
                  aria-label="Logout"
                  title="Logout"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="hidden h-10 items-center rounded-xl border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-100 sm:inline-flex"
                >
                  Logout
                </button>
              </>
            )}
          </div>
        </div>

      </div>

      {(errorMessage || (message && message.text)) && (
        <div
          className="pointer-events-none fixed inset-x-0 z-[80] flex justify-center px-3"
          style={{ top: 'calc(var(--safe-top, env(safe-area-inset-top, 0px)) + 0.5rem)' }}
          role="alert"
        >
          <div
            className={`pointer-events-auto flex max-w-lg w-full items-start gap-2 rounded-2xl border px-3 py-2.5 text-sm font-semibold shadow-lg backdrop-blur ${
              errorMessage || (message && message.type === 'error')
                ? 'border-red-300 bg-red-50/95 text-red-900 dark:border-red-700 dark:bg-red-950/95 dark:text-red-100'
                : 'border-emerald-300 bg-emerald-50/95 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/95 dark:text-emerald-100'
            }`}
          >
            <p className="min-w-0 flex-1 leading-snug">{errorMessage || message.text}</p>
            <button
              type="button"
              className="shrink-0 rounded-lg px-2 py-0.5 text-base leading-none opacity-70 hover:opacity-100"
              aria-label="Chiudi"
              onClick={() => {
                setErrorMessage('');
                setMessage(null);
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {cloneRequestsInbox.length > 0 && (
        <div className="fixed bottom-28 right-4 z-50 w-[92vw] max-w-md bg-white dark:bg-gray-800 border border-indigo-300 dark:border-indigo-700 rounded-lg shadow-xl p-4 sm:bottom-4">
          <h4 className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
            Richiesta di condivisione
          </h4>
          <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
            <strong>{cloneRequestsInbox[0]?.sourceUser?.username || 'Un utente'}</strong> vuole condividere dati sul tuo account.
          </p>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            Copiabili: S:{cloneRequestsInbox[0]?.previewReport?.copied?.songs || 0} C:{cloneRequestsInbox[0]?.previewReport?.copied?.choreographies || 0} A:{cloneRequestsInbox[0]?.previewReport?.copied?.associations || 0}
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => handleDecideCloneRequest(cloneRequestsInbox[0].id, true)}
              disabled={cloneRequestActionLoading}
              className={`px-3 py-2 rounded text-xs text-white ${cloneRequestActionLoading ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
            >
              {cloneRequestActionLoading ? 'Elaborazione...' : 'Accetta'}
            </button>
            <button
              type="button"
              onClick={() => handleDecideCloneRequest(cloneRequestsInbox[0].id, false)}
              disabled={cloneRequestActionLoading}
              className={`px-3 py-2 rounded text-xs text-white ${cloneRequestActionLoading ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}
            >
              {cloneRequestActionLoading ? 'Elaborazione...' : 'Rifiuta'}
            </button>
          </div>
          {cloneRequestActionLoading && (
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
              <span className="inline-block w-4 h-4 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
              Aggiornamento dati in corso...
            </div>
          )}
        </div>
      )}

      {/* Menu laterale (fuori dall'header per evitare problemi di z-index/overlay) */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 z-[90] bg-black/50"
          onClick={() => setIsMenuOpen(false)}
          aria-hidden
        />
      )}
      <div
        className={`fixed inset-y-0 right-0 z-[100] w-[min(20rem,92vw)] overflow-visible bg-white shadow-2xl transition-transform duration-300 ease-in-out dark:bg-gray-800 ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full pointer-events-none'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Menu impostazioni"
      >
        <button
          type="button"
          className="menu-drawer-handle"
          onClick={() => setIsMenuOpen(false)}
          aria-label="Chiudi menu"
          style={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 22,
            height: 56,
            borderRadius: '0 10px 10px 0',
            border: 'none',
            padding: 0,
            background: 'rgba(107,114,128,0.35)',
            zIndex: 20,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span
            aria-hidden
            style={{
              display: 'block',
              width: 4,
              height: 36,
              borderRadius: 9999,
              background: '#d1d5db',
            }}
          />
        </button>
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">Menu</h2>
            <button
              type="button"
              onClick={() => setIsMenuOpen(false)}
              className="hidden rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white sm:inline-flex"
              aria-label="Chiudi menu"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto pb-6">
            <div className="m-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
              <h3 className="mb-2 text-lg font-semibold text-indigo-600 dark:text-indigo-400">Informazioni</h3>
              <div className="mb-2 flex items-center">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={handleTermsCheckboxChange}
                  disabled={isSavingTerms}
                  id="acceptTermsMenu"
                  className="mr-2 h-4 w-4"
                />
                <label htmlFor="acceptTermsMenu" className="text-sm text-gray-700 dark:text-gray-200">
                  Accetto i termini e le condizioni
                  {isSavingTerms ? '…' : ''}
                </label>
              </div>
              {errorMessage && errorMessage.toLowerCase().includes('termin') && (
                <p className="mb-2 text-xs text-red-600 dark:text-red-300">{errorMessage}</p>
              )}
              {termsAccepted && termsLastAccepted && (
                <p className="mb-2 text-xs text-green-700 dark:text-green-300">
                  Accettati il {new Date(termsLastAccepted).toLocaleString('it-IT')}
                </p>
              )}
              <button
                type="button"
                className="mt-2 text-xs text-indigo-600 underline hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200"
                onClick={() => setShowDisclaimer(true)}
              >
                Disclaimer
              </button>
              <button
                type="button"
                className="ml-3 mt-2 text-xs text-indigo-600 underline hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200"
                onClick={() => {
                  setIsMenuOpen(false);
                  setShowProfileModal(true);
                }}
              >
                Il mio profilo
              </button>
            </div>
            <div
              className="m-4 cursor-pointer rounded-lg bg-gray-50 p-4 transition hover:bg-indigo-50 dark:bg-gray-700 dark:hover:bg-indigo-900"
              onClick={() => {
                openShareTab();
                setIsMenuOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  openShareTab();
                  setIsMenuOpen(false);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <h3 className="mb-2 text-lg font-semibold text-indigo-600 dark:text-indigo-400">Condividi</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Apri la sezione per condividere i tuoi dati con un altro utente
              </p>
            </div>
            <p className="mt-auto px-4 pt-2 pb-1 text-center text-xs text-gray-400 dark:text-gray-500">
              Versione {APP_VERSION}
            </p>
          </div>
        </div>
      </div>

      <div
        className={`max-w-7xl mx-auto sm:pb-0 ${
          (mainAppTab === 'songs' || mainAppTab === 'choreographies' || mainAppTab === 'associations' || mainAppTab === 'share')
            ? 'pb-1 flex h-[calc(100dvh-8.8rem)] flex-col overflow-hidden'
            : 'pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))]'
        }`}
      >
        {/* Barra di ricerca */}
        <div className="sticky top-0 z-20 mb-0.5 bg-gray-100 pb-2 pt-1 dark:bg-gray-900 sm:static sm:mb-8 sm:bg-transparent sm:pb-0 sm:pt-0">
          <div className="relative">
            <input
              type="search"
              name="country-hub-search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={
                mainAppTab === 'associations'
                  ? 'Cerca nelle associazioni...'
                  : mainAppTab === 'songs'
                    ? 'Cerca nelle canzoni...'
                    : mainAppTab === 'share'
                      ? 'Condividi dati con un altro utente'
                      : 'Cerca nelle coreografie...'
              }
              className="w-full px-4 py-3 pl-12 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-all duration-200"
            />
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Tab desktop: canzoni, coreografie, associazioni */}
        <div className="hidden sm:flex mb-6 gap-2 p-1 rounded-xl bg-gray-200/80 dark:bg-gray-700/80 w-full max-w-2xl">
          <button
            type="button"
            onClick={() => { closeTraining(); setMainAppTab('songs'); }}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition ${!showTraining && mainAppTab === 'songs' ? 'bg-white dark:bg-gray-800 text-indigo-700 dark:text-indigo-300 shadow' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}
          >
            Musica
          </button>
          <button
            type="button"
            onClick={() => { closeTraining(); setMainAppTab('choreographies'); }}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition ${!showTraining && mainAppTab === 'choreographies' ? 'bg-white dark:bg-gray-800 text-indigo-700 dark:text-indigo-300 shadow' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}
          >
            Coreografie
          </button>
          <button
            type="button"
            onClick={() => { closeTraining(); setMainAppTab('associations'); }}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition ${!showTraining && mainAppTab === 'associations' ? 'bg-white dark:bg-gray-800 text-indigo-700 dark:text-indigo-300 shadow' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}
          >
            Associazioni
          </button>
          <button
            type="button"
            onClick={openTraining}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition ${showTraining ? 'bg-white dark:bg-gray-800 text-indigo-700 dark:text-indigo-300 shadow' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}
          >
            Training
          </button>
          <button
            type="button"
            onClick={openShareTab}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition ${!showTraining && mainAppTab === 'share' ? 'bg-white dark:bg-gray-800 text-indigo-700 dark:text-indigo-300 shadow' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'}`}
          >
            Condividi
          </button>
        </div>

        {/* Sezioni principali */}
        {(mainAppTab === 'songs' || mainAppTab === 'choreographies') && (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-0">
          {mainAppTab === 'songs' && (
          <div className="mb-0 bg-white dark:bg-gray-800 rounded-xl shadow-md p-2.5 sm:mb-0 sm:p-4 flex h-full min-h-0 flex-col overflow-hidden">
            <div className="flex justify-between items-center mb-2 sm:mb-4">
              <h2 className="section-title section-title--songs">
                Le Tue Canzoni
              </h2>
              {/* --- INIZIO MODIFICA UI BOTTONI --- */}
              {/* Bottone classico su desktop */}
              <button
                onClick={openNewSongModal}
                className="btn-section--songs hidden sm:flex items-center px-4 py-2 rounded-lg transition hover:opacity-90"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Nuova Canzone
              </button>
              {/* FAB solo su mobile per Nuova Canzone */}
              {isLoggedIn && cloneMode !== 'selected' && (
                <button
                  onClick={openNewSongModal}
                  className="btn-section--songs fab-above-dock fixed right-4 z-50 rounded-2xl p-3.5 ring-2 ring-white/20 transition active:scale-95 sm:hidden dark:ring-gray-900/40"
                  title="Nuova Canzone"
                  style={{ boxShadow: '0 4px 14px rgba(29, 78, 216, 0.35)' }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              )}
              {/* --- FINE MODIFICA UI BOTTONI --- */}
            </div>
            <div className="mobile-list-shell min-h-0 flex-1 overflow-hidden pb-2 sm:pb-0 sm:overflow-visible sm:border-0 sm:bg-transparent">
            <TouchHandler
              onPullToRefresh={() => {
                if (!requireOnline('Aggiornamento')) return;
                loadMainLists({ cache: 'no-store' });
              }}
            >
            {filteredSongs.length === 0 ? ( 
              <p className="text-gray-600 dark:text-gray-400 italic">Nessuna canzone trovata con il termine di ricerca. {searchTerm ? '' : 'Aggiungine una sopra!'}</p>
            ) : (
              <ul className="mobile-list flex flex-col gap-2 sm:gap-3">
                {filteredSongs.map((song) => {
                  const sid = String(song.id);
                  const rowId = `song:${sid}`;
                  const selected = cloneMode === 'selected' && cloneSelectedSongIds.includes(sid);
                  const mediaType = getMediaType(song);
                  const hasMedia = Boolean(song.url) || mediaType !== 'unknown';
                  const expanded = expandedSongMediaIds.includes(sid);
                  const toggleMedia = () => setExpandedSongMediaIds((prev) =>
                    prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]
                  );
                  const swipeActions = [
                    hasMedia && {
                      key: 'play',
                      label: 'Play',
                      tone: 'play',
                      icon: (<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.868v4.264a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>),
                      onClick: () => toggleMedia()
                    },
                    {
                      key: 'edit',
                      label: 'Modifica',
                      tone: 'edit',
                      icon: (<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536M9 13l6-6m2 2l-6 6m-2 2h2v2h-2z" /></svg>),
                      onClick: () => handleEditSong(song)
                    },
                    {
                      key: 'del',
                      label: 'Elimina',
                      tone: 'danger',
                      icon: (<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-7 0h8" /></svg>),
                      onClick: () => confirmDelete('song', song.id)
                    }
                  ].filter(Boolean);
                  return (
                    <li key={song.id} className="list-none">
                      <SwipeRow
                        rowId={rowId}
                        openId={openSwipeId}
                        onOpenChange={setOpenSwipeId}
                        disabled={cloneMode === 'selected'}
                        actions={swipeActions}
                        className={`mobile-tile ${selected ? 'is-selected' : ''}`}
                        contentClassName="tile-surface"
                        shareHandlers={getShareTileHandlers('song', song.id)}
                      >
                        <div className="flex items-center gap-3">
                          {cloneMode === 'selected' && (
                            <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold ${selected ? 'border-yellow-600 bg-yellow-500 text-white' : 'border-gray-300 bg-white text-transparent dark:border-gray-500 dark:bg-gray-800'}`}>
                              ✓
                            </span>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="mobile-tile-title truncate text-gray-900 dark:text-gray-100">{song.title}</p>
                            <p className="mobile-tile-meta truncate text-gray-500 dark:text-gray-400">
                              {song.artist}{song.genre ? ` · ${song.genre}` : ''}
                            </p>
                          </div>
                          {cloneMode !== 'selected' && (
                            <div className="mobile-inline-actions hidden shrink-0 items-center gap-1 sm:flex">
                              {hasMedia && (
                                <button type="button" className="tile-btn tile-btn-play" title="Media" onClick={() => toggleMedia()}>
                                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.868v4.264a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </button>
                              )}
                              <button type="button" className="tile-btn tile-btn-edit" title="Modifica" onClick={() => handleEditSong(song)}>
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536M9 13l6-6m2 2l-6 6m-2 2h2v2h-2z" /></svg>
                              </button>
                              <button type="button" className="tile-btn tile-btn-danger" title="Elimina" onClick={() => confirmDelete('song', song.id)}>
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-7 0h8" /></svg>
                              </button>
                            </div>
                          )}
                        </div>
                        {hasMedia && (
                          <div className={`mt-2 ${expanded ? 'block' : 'hidden'} sm:block`}>
                            {mediaType === 'audio' ? (
                              <audio controls className="w-full mobile-tile-media">
                                <source src={getStreamingUrl(song.url)} type={song.mimeType || 'audio/mpeg'} />
                              </audio>
                            ) : mediaType === 'youtube' ? (
                              <iframe
                                src={getStreamingUrl(song.url)}
                                title={song.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="mobile-tile-media h-36 w-full rounded-lg sm:h-48"
                              ></iframe>
                            ) : song.url ? (
                              <a href={song.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 dark:bg-indigo-950 dark:text-indigo-200">
                                Apri in {song.url.includes('spotify.com') ? 'Spotify' : 'Browser'}
                              </a>
                            ) : null}
                          </div>
                        )}
                      </SwipeRow>
                    </li>
                  );
                })}
              </ul>
            )}
            </TouchHandler>
            </div>
          </div>
          )}

          {mainAppTab === 'choreographies' && (
          <div className="mb-0 bg-white dark:bg-gray-800 rounded-xl shadow-md p-2.5 sm:mb-0 sm:p-4 flex h-full min-h-0 flex-col overflow-hidden">
            <div className="flex justify-between items-center mb-2 sm:mb-4">
              <h2 className="section-title section-title--choreo">
                Le Tue Coreografie
              </h2>
              {/* Bottone classico su desktop */}
              <button
                onClick={openNewChoreographyModal}
                className="btn-section--choreo hidden sm:flex items-center px-4 py-2 rounded-lg transition hover:opacity-90"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Nuova Coreografia
              </button>
              {/* FAB solo su mobile per Nuova Coreografia */}
              {isLoggedIn && cloneMode !== 'selected' && (
                <button
                  onClick={openNewChoreographyModal}
                  className="btn-section--choreo fab-above-dock fixed right-4 z-50 rounded-2xl p-3.5 ring-2 ring-white/20 transition active:scale-95 sm:hidden dark:ring-gray-900/40"
                  title="Nuova Coreografia"
                  style={{ boxShadow: '0 4px 14px rgba(4, 120, 87, 0.35)' }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              )}
            </div>
            <div className="mobile-list-shell min-h-0 flex-1 overflow-hidden pb-2 sm:pb-0 sm:overflow-visible sm:border-0 sm:bg-transparent">
            <TouchHandler
              onPullToRefresh={() => {
                if (!requireOnline('Aggiornamento')) return;
                loadMainLists({ cache: 'no-store' });
              }}
            >
            {filteredChoreographies.length === 0 ? ( 
              <p className="text-gray-600 dark:text-gray-400 italic">Nessuna coreografia trovata con il termine di ricerca. {searchTerm ? '' : 'Aggiungine una sopra!'}</p>
            ) : (
              <ul className="mobile-list flex flex-col gap-2 sm:gap-3">
                {filteredChoreographies.map((choreo) => {
                  const cid = String(choreo.id);
                  const rowId = `choreo:${cid}`;
                  const expandKey = rowId;
                  const selected = cloneMode === 'selected' && cloneSelectedChoreoIds.includes(cid);
                  const mediaList = getChoreographyMediaList(choreo);
                  const expanded = expandedChoreoMediaIds.includes(expandKey);
                  const swipeActions = [
                    mediaList.length > 0 && {
                      key: 'play',
                      label: mediaList.length > 1 ? 'Link' : 'Play',
                      tone: 'play',
                      icon: (<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.868v4.264a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>),
                      onClick: () => playOrExpandChoreoMedia(choreo, expandKey)
                    },
                    {
                      key: 'edit',
                      label: 'Modifica',
                      tone: 'edit',
                      icon: (<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536M9 13l6-6m2 2l-6 6m-2 2h2v2h-2z" /></svg>),
                      onClick: () => handleEditChoreography(choreo)
                    },
                    {
                      key: 'del',
                      label: 'Elimina',
                      tone: 'danger',
                      icon: (<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-7 0h8" /></svg>),
                      onClick: () => confirmDelete('choreography', choreo.id)
                    }
                  ].filter(Boolean);
                  return (
                    <li key={choreo.id} className="list-none">
                      <SwipeRow
                        rowId={rowId}
                        openId={openSwipeId}
                        onOpenChange={setOpenSwipeId}
                        disabled={cloneMode === 'selected'}
                        actions={swipeActions}
                        className={`mobile-tile ${selected ? 'is-selected' : ''} ${expanded ? 'is-expanded' : ''}`}
                        contentClassName="tile-surface"
                        shareHandlers={getShareTileHandlers('choreo', choreo.id)}
                      >
                        <div
                          className="flex items-center gap-3"
                          onClick={(e) => {
                            if (cloneMode === 'selected') return;
                            if (!mediaList.length) return;
                            if (e.target?.closest?.('button, a, input, audio, video, iframe, label')) return;
                            playOrExpandChoreoMedia(choreo, expandKey);
                          }}
                        >
                          {cloneMode === 'selected' && (
                            <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold ${selected ? 'border-yellow-600 bg-yellow-500 text-white' : 'border-gray-300 bg-white text-transparent dark:border-gray-500 dark:bg-gray-800'}`}>
                              ✓
                            </span>
                          )}
                          {renderChoreographyThumb(choreo, (mediaItem) => {
                            if (mediaList.length > 1) {
                              toggleChoreoMediaExpand(expandKey);
                              return;
                            }
                            const first = pickChoreographyThumbMedia(choreo) || mediaList[0];
                            handlePlayMedia({
                              ...mediaItem,
                              mediaId: first?.id || null,
                              sourceType: first?.sourceType || '',
                              choreographyId: choreo.id,
                              isChoreographyOnly: true
                            });
                          })}
                          <div className="min-w-0 flex-1">
                            <p className="mobile-tile-title truncate text-gray-900 dark:text-gray-100">{choreo.title}</p>
                            <p className="mobile-tile-meta truncate text-gray-500 dark:text-gray-400">
                              {choreo.artist || '—'}
                              {choreo.difficulty ? ` · ${choreo.difficulty}` : ''}
                              {mediaList.length > 1 ? ` · ${mediaList.length} link` : ''}
                            </p>
                          </div>
                          {mediaList.length > 1 && cloneMode !== 'selected' && (
                            <button
                              type="button"
                              className="tile-expand-chevron sm:hidden"
                              aria-expanded={expanded}
                              aria-label={expanded ? 'Nascondi link' : 'Mostra link'}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleChoreoMediaExpand(expandKey);
                              }}
                            >
                              <svg className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          )}
                          {cloneMode !== 'selected' && (
                            <div className="mobile-inline-actions hidden shrink-0 items-center gap-1 sm:flex">
                              {mediaList.length > 0 && (
                                <button type="button" className="tile-btn tile-btn-play" title="Riproduci" onClick={() => handleTrainingPlay(choreo)}>
                                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.868v4.264a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </button>
                              )}
                              <button type="button" className="tile-btn tile-btn-edit" title="Modifica" onClick={() => handleEditChoreography(choreo)}>
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536M9 13l6-6m2 2l-6 6m-2 2h2v2h-2z" /></svg>
                              </button>
                              <button type="button" className="tile-btn tile-btn-danger" title="Elimina" onClick={() => confirmDelete('choreography', choreo.id)}>
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-7 0h8" /></svg>
                              </button>
                            </div>
                          )}
                        </div>
                        {renderChoreographyMediaChips(choreo, { expanded })}
                      </SwipeRow>
                    </li>
                  );
                })}
              </ul>
            )}
            </TouchHandler>
            </div>
          </div>
          )}
        </div>
        )}

        {/* Sezione Associazioni (vista dedicata) */}
        {mainAppTab === 'associations' && (
        <div className="mt-0 sm:mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-md p-2.5 sm:p-4 flex h-full min-h-0 flex-col overflow-hidden">
          <div className="flex justify-between items-center mb-2 sm:mb-4">
            <h2 className="section-title section-title--assoc">
              Le Tue Associazioni
            </h2>
            <button
              type="button"
              onClick={openNewAssociationModal}
              className="btn-section--assoc hidden sm:flex items-center px-4 py-2 rounded-lg transition hover:opacity-90"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Nuova Associazione
            </button>
            {isLoggedIn && cloneMode !== 'selected' && (
              <button
                type="button"
                onClick={openNewAssociationModal}
                className="btn-section--assoc fab-above-dock fixed right-4 z-50 rounded-2xl p-3.5 ring-2 ring-white/20 transition active:scale-95 sm:hidden dark:ring-gray-900/40"
                title="Nuova Associazione"
                style={{ boxShadow: '0 4px 14px rgba(67, 70, 217, 0.35)' }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
              </button>
            )}
          </div>
          <div className="mobile-list-shell min-h-0 flex-1 overflow-hidden pb-2 sm:pb-0 sm:overflow-visible sm:border-0 sm:bg-transparent">
          <TouchHandler
            onPullToRefresh={() => {
              if (!requireOnline('Aggiornamento')) return;
              loadMainLists({ cache: 'no-store' });
            }}
          >
          {/* Lista delle associazioni esistenti */}
          {associations.length === 0 ? (
            <p className="text-gray-600 dark:text-gray-400 italic">Nessuna associazione creata. Usa il pulsante Nuova Associazione per iniziare.</p>
          ) : (
            <div className="mobile-list grid grid-cols-1 gap-2 sm:gap-4">
              {associations
                .filter(association => {
                  const song = songs.find(s => s.id === association.songId);
                  const choreography = choreographies.find(c => c.id === association.choreographyId);
                  
                  if (!song || !choreography) return false;
                  
                  const searchLower = searchTerm.toLowerCase();
                  return (
                    song.title.toLowerCase().includes(searchLower) ||
                    song.artist.toLowerCase().includes(searchLower) ||
                    song.genre?.toLowerCase().includes(searchLower) ||
                    choreography.title.toLowerCase().includes(searchLower) ||
                    choreography.artist.toLowerCase().includes(searchLower) ||
                    choreography.difficulty?.toLowerCase().includes(searchLower) ||
                    association.notes?.toLowerCase().includes(searchLower)
                  );
                })
                .map((association) => {
                  const song = songs.find(s => s.id === association.songId);
                  const choreography = choreographies.find(c => c.id === association.choreographyId);
                  const expandKey = `assoc:${association.id}`;
                  const mediaList = choreography ? getChoreographyMediaList(choreography) : [];
                  const expanded = expandedChoreoMediaIds.includes(expandKey);
                  const selected = cloneMode === 'selected' && cloneSelectedAssociationIds.includes(String(association.id));
                  
                  const choreographyMediaType = choreography ? getMediaType(choreography) : 'unknown';
                  const youtubeEmbedUrl = choreography ? getYouTubeEmbedUrl(choreography.url) : null;

                  return (
                      <SwipeRow
                        key={association.id}
                        rowId={expandKey}
                        openId={openSwipeId}
                        onOpenChange={setOpenSwipeId}
                        disabled={cloneMode === 'selected'}
                        actions={[
                          song && {
                            key: 'song',
                            label: 'Canzone',
                            tone: 'play',
                            icon: (<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" /></svg>),
                            onClick: () => handlePlayMedia(song)
                          },
                          choreography && mediaList.length > 0 && {
                            key: 'choreo',
                            label: mediaList.length > 1 ? 'Link' : 'Play',
                            tone: 'play',
                            icon: (<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.868v4.264a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>),
                            onClick: () => playOrExpandChoreoMedia(choreography, expandKey)
                          },
                          {
                            key: 'edit',
                            label: 'Modifica',
                            tone: 'edit',
                            icon: (<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536M9 13l6-6m2 2l-6 6m-2 2h2v2h-2z" /></svg>),
                            onClick: () => handleEditAssociation(association)
                          },
                          {
                            key: 'del',
                            label: 'Elimina',
                            tone: 'danger',
                            icon: (<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-7 0h8" /></svg>),
                            onClick: () => handleDeleteAssociation(association.id)
                          }
                        ].filter(Boolean)}
                        className={`mobile-tile ${selected ? 'is-selected' : ''} ${expanded ? 'is-expanded' : ''}`}
                        contentClassName="tile-surface"
                        shareHandlers={getShareTileHandlers('association', association.id)}
                      >
                        <div
                          className="flex items-center gap-3"
                          onClick={(e) => {
                            if (cloneMode === 'selected') return;
                            if (!choreography || !mediaList.length) return;
                            if (e.target?.closest?.('button, a, input, audio, video, iframe, label')) return;
                            playOrExpandChoreoMedia(choreography, expandKey);
                          }}
                        >
                          {cloneMode === 'selected' && (
                            <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold ${selected ? 'border-yellow-600 bg-yellow-500 text-white' : 'border-gray-300 bg-white text-transparent dark:border-gray-500 dark:bg-gray-800'}`}>
                              ✓
                            </span>
                          )}
                          {choreography && renderChoreographyThumb(choreography, (mediaItem) => {
                            if (mediaList.length > 1) {
                              toggleChoreoMediaExpand(expandKey);
                              return;
                            }
                            const first = pickChoreographyThumbMedia(choreography) || mediaList[0];
                            handlePlayMedia({
                              ...mediaItem,
                              mediaId: first?.id || null,
                              sourceType: first?.sourceType || '',
                              choreographyId: choreography.id,
                              isChoreographyOnly: true
                            });
                          })}
                          <div className="min-w-0 flex-1">
                            <p className="mobile-tile-title truncate text-gray-900 dark:text-gray-100">
                              {song ? song.title : 'Canzone'}
                            </p>
                            <p className="mobile-tile-meta truncate text-gray-500 dark:text-gray-400">
                              {song ? song.artist : ''}
                              {choreography ? ` → ${choreography.title}` : ''}
                              {choreography?.difficulty ? ` · ${choreography.difficulty}` : ''}
                              {mediaList.length > 1 ? ` · ${mediaList.length} link` : ''}
                            </p>
                          </div>
                          {mediaList.length > 1 && cloneMode !== 'selected' && (
                            <button
                              type="button"
                              className="tile-expand-chevron sm:hidden"
                              aria-expanded={expanded}
                              aria-label={expanded ? 'Nascondi link' : 'Mostra link'}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleChoreoMediaExpand(expandKey);
                              }}
                            >
                              <svg className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          )}
                          {cloneMode !== 'selected' && (
                            <div className="mobile-inline-actions hidden shrink-0 items-center gap-1 sm:flex">
                              {song && (
                                <button type="button" className="tile-btn tile-btn-play" title="Solo canzone" onClick={() => handlePlayMedia(song)}>
                                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" /></svg>
                                </button>
                              )}
                              {choreography && (
                                <button type="button" className="tile-btn tile-btn-play" title="Coreografia" onClick={() => handleTrainingPlay(choreography)}>
                                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.868v4.264a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                </button>
                              )}
                              <button type="button" className="tile-btn tile-btn-edit" title="Modifica" onClick={() => handleEditAssociation(association)}>
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536M9 13l6-6m2 2l-6 6m-2 2h2v2h-2z" /></svg>
                              </button>
                              <button type="button" className="tile-btn tile-btn-danger" title="Elimina" onClick={() => handleDeleteAssociation(association.id)}>
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-7 0h8" /></svg>
                              </button>
                            </div>
                          )}
                        </div>
                        {choreography && renderChoreographyMediaChips(choreography, { expanded })}
                        <div className="mobile-assoc-preview mt-3 hidden md:block">
                          {choreography && choreographyMediaType === 'youtube' && youtubeEmbedUrl ? (
                            <div className="relative h-0 overflow-hidden rounded-lg pb-[56.25%]">
                              <iframe
                                src={youtubeEmbedUrl}
                                title={`YouTube video for ${choreography.title}`}
                                className="absolute left-0 top-0 h-full w-full rounded-lg"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              ></iframe>
                            </div>
                          ) : choreography && choreographyMediaType === 'video' ? (
                            <video controls className="mt-2 w-full rounded-lg">
                              <source src={getStreamingUrl(choreography.url)} type={choreography.mimeType || 'video/mp4'} />
                            </video>
                          ) : null}
                        </div>
                      </SwipeRow>
                  );
                })}
            </div>
          )}
          </TouchHandler>
          </div>
        </div>
        )}


        {mainAppTab === 'share' && (
        <div className="mt-0 sm:mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-md p-3 sm:p-4 flex h-full min-h-0 flex-col overflow-hidden">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="text-xl sm:text-2xl font-bold text-indigo-700 dark:text-indigo-400">
              Condividi
            </h2>
            <button
              type="button"
              onClick={closeShareTab}
              className="shrink-0 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              Chiudi
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto pr-1 pb-2 space-y-4">
            <div className="rounded-xl border border-indigo-200 bg-indigo-50/80 p-3 dark:border-indigo-700 dark:bg-indigo-950/40">
              <p className="text-sm font-semibold text-indigo-800 dark:text-indigo-200">Come condividere</p>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
                <li>Vai in Musica, Coreografie o Associazioni</li>
                <li>Tieni premuto su una tile per entrare in selezione</li>
                <li>Tocca altre tile</li>
                <li><strong>Link</strong> (copia e incolla), <strong>App</strong> (WhatsApp/Telegram) oppure <strong>Utente</strong> (account in-app)</li>
              </ol>
              <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                Su desktop: tasto destro sulla tile = selezione. Il link permette di visualizzare i contenuti; chi ha l’app può anche importarli. Scade dopo 90 giorni.
              </p>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Richieste in arrivo</h3>
                <button
                  type="button"
                  onClick={loadCloneRequestsInbox}
                  className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-[11px] font-semibold text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  title="Ricarica le richieste di condivisione ricevute"
                >
                  Aggiorna richieste
                </button>
              </div>
              {cloneRequestsInbox.length === 0 ? (
                <p className="text-sm italic text-gray-500 dark:text-gray-400">Nessuna richiesta in sospeso.</p>
              ) : (
                <ul className="space-y-2">
                  {cloneRequestsInbox.map((req) => (
                    <li
                      key={req.id}
                      className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-900"
                    >
                      <p className="text-sm text-gray-800 dark:text-gray-200">
                        <strong>{req?.sourceUser?.username || 'Un utente'}</strong> vuole condividere dati con te.
                      </p>
                      <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                        Copiabili: S:{req?.previewReport?.copied?.songs || 0} C:{req?.previewReport?.copied?.choreographies || 0} A:{req?.previewReport?.copied?.associations || 0}
                      </p>
                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleDecideCloneRequest(req.id, true)}
                          disabled={cloneRequestActionLoading}
                          className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold text-white ${cloneRequestActionLoading ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
                        >
                          Accetta
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDecideCloneRequest(req.id, false)}
                          disabled={cloneRequestActionLoading}
                          className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold text-white ${cloneRequestActionLoading ? 'bg-gray-400' : 'bg-red-600 hover:bg-red-700'}`}
                        >
                          Rifiuta
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        )}

      {/* Pannello selezione condivisione: in-flow, riduce l’area lista */}
      {isLoggedIn && cloneMode === 'selected' && (
        <div className="share-selection-panel shrink-0 mt-0.5 mb-0.5 rounded-xl border border-yellow-400 bg-white px-2 py-1.5 shadow-sm dark:border-yellow-600 dark:bg-gray-900 sm:mb-1 sm:px-2.5 sm:py-2">
          <div className="mb-1 flex items-center justify-between gap-2">
            <p className="min-w-0 truncate text-[11px] font-semibold text-gray-900 dark:text-gray-100">
              {selectedShareCount} sel. · C {cloneSelectedSongIds.length} · Co {cloneSelectedChoreoIds.length} · A {cloneSelectedAssociationIds.length}
            </p>
            <button
              type="button"
              onClick={exitShareSelection}
              className="shrink-0 rounded-lg border border-gray-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              Annulla
            </button>
          </div>
          <div className="grid grid-cols-4 gap-1 sm:gap-1.5">
            <button
              type="button"
              onClick={selectAllVisibleForShare}
              className="rounded-lg border border-gray-300 bg-white px-1 py-1 text-[10px] font-semibold leading-tight text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              Tutto
            </button>
            <button
              type="button"
              onClick={copyShareLinkOnly}
              disabled={selectedShareCount === 0 || shareLinkCreating}
              className={`rounded-lg px-1 py-1 text-[10px] font-bold leading-tight text-white ${selectedShareCount === 0 || shareLinkCreating ? 'bg-gray-400' : 'bg-sky-600 hover:bg-sky-700'}`}
              title="Copia link da incollare ovunque"
            >
              Link
            </button>
            <button
              type="button"
              onClick={handleExternalShare}
              disabled={selectedShareCount === 0 || shareLinkCreating}
              className={`rounded-lg px-1 py-1 text-[10px] font-bold leading-tight text-white ${selectedShareCount === 0 || shareLinkCreating ? 'bg-gray-400' : 'bg-emerald-600 hover:bg-emerald-700'}`}
              title="WhatsApp, Telegram e altre app"
            >
              App
            </button>
            <button
              type="button"
              onClick={openShareRecipientModal}
              disabled={selectedShareCount === 0}
              className={`rounded-lg px-1 py-1 text-[10px] font-bold leading-tight text-white ${selectedShareCount === 0 ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}
              title="Invia a un utente dell'app"
            >
              Utente
            </button>
          </div>
        </div>
      )}

      {/* Navigazione mobile: dock separata dal contenuto */}
      <nav
        className="mobile-dock pointer-events-none fixed bottom-0 left-0 right-0 z-30 sm:hidden"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        aria-label="Sezioni principali"
      >
        <div className="mobile-dock-inner pointer-events-auto flex w-full gap-2 border border-gray-200 bg-white p-1 shadow-[0_-4px_24px_rgba(0,0,0,0.08),0_8px_32px_rgba(99,102,241,0.12)] dark:border-gray-600 dark:bg-gray-900 dark:shadow-[0_-4px_24px_rgba(0,0,0,0.35),0_8px_32px_rgba(99,102,241,0.15)]">
          <button
            type="button"
            onClick={() => { closeTraining(); setMainAppTab('songs'); }}
            className={`flex min-h-[2.1rem] flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-0 transition-all duration-200 ${
              !showTraining && mainAppTab === 'songs'
                ? 'bg-gradient-to-b from-indigo-500 to-indigo-600 text-white shadow-md shadow-indigo-500/30'
                : 'text-gray-500 hover:bg-gray-100 active:scale-[0.98] dark:text-gray-400 dark:hover:bg-gray-800/80'
            }`}
          >
            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
            <span className="max-w-[5.5rem] text-center text-[9px] font-semibold leading-tight tracking-wide">Musica</span>
          </button>
          <button
            type="button"
            onClick={() => { closeTraining(); setMainAppTab('choreographies'); }}
            className={`flex min-h-[2.1rem] flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-0 transition-all duration-200 ${
              !showTraining && mainAppTab === 'choreographies'
                ? 'bg-gradient-to-b from-indigo-500 to-indigo-600 text-white shadow-md shadow-indigo-500/30'
                : 'text-gray-500 hover:bg-gray-100 active:scale-[0.98] dark:text-gray-400 dark:hover:bg-gray-800/80'
            }`}
          >
            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.868v4.264a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="max-w-[5.5rem] text-center text-[9px] font-semibold leading-tight tracking-wide">Coreografie</span>
          </button>
          <button
            type="button"
            onClick={() => { closeTraining(); setMainAppTab('associations'); }}
            className={`flex min-h-[2.1rem] flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-0 transition-all duration-200 ${
              !showTraining && mainAppTab === 'associations'
                ? 'bg-gradient-to-b from-indigo-500 to-indigo-600 text-white shadow-md shadow-indigo-500/30'
                : 'text-gray-500 hover:bg-gray-100 active:scale-[0.98] dark:text-gray-400 dark:hover:bg-gray-800/80'
            }`}
          >
            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            <span className="max-w-[5.5rem] text-center text-[9px] font-semibold leading-tight tracking-wide">Associazioni</span>
          </button>
          <button
            type="button"
            onClick={openTraining}
            className={`flex min-h-[2.1rem] flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-0 transition-all duration-200 ${
              showTraining
                ? 'bg-gradient-to-b from-indigo-500 to-indigo-600 text-white shadow-md shadow-indigo-500/30'
                : 'text-gray-500 hover:bg-gray-100 active:scale-[0.98] dark:text-gray-400 dark:hover:bg-gray-800/80'
            }`}
          >
            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <span className="max-w-[5.5rem] text-center text-[9px] font-semibold leading-tight tracking-wide">Training</span>
          </button>
          <button
            type="button"
            onClick={openShareTab}
            className={`relative flex min-h-[2.1rem] flex-1 flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-0 transition-all duration-200 ${
              !showTraining && mainAppTab === 'share'
                ? 'bg-gradient-to-b from-indigo-500 to-indigo-600 text-white shadow-md shadow-indigo-500/30'
                : 'text-gray-500 hover:bg-gray-100 active:scale-[0.98] dark:text-gray-400 dark:hover:bg-gray-800/80'
            }`}
          >
            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <span className="max-w-[5.5rem] text-center text-[9px] font-semibold leading-tight tracking-wide">Condividi</span>
            {cloneRequestsInbox.length > 0 && (
              <span className="absolute -top-1 right-1 min-w-[14px] h-[14px] rounded-full bg-red-600 text-[9px] leading-[14px] text-center text-white font-bold">
                {cloneRequestsInbox.length}
              </span>
            )}
          </button>
        </div>
      </nav>
      </div>

      {/* Modals */}
      <dialog ref={playMediaModalRef} className="player-immersive p-3 sm:p-6 rounded-lg shadow-xl bg-white dark:bg-gray-800 max-w-4xl w-[95vw] sm:w-full h-[100dvh] sm:h-[80vh]">
        <div className="flex justify-between items-start mb-3 sm:mb-4">
          <h3 className="text-lg sm:text-xl font-bold text-indigo-700 dark:text-indigo-400">
            {playingMedia?.title} - {playingMedia?.artist}
          </h3>
          <button
            onClick={handleClosePlayMedia}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        {playingMedia && (
          <div className="space-y-3 sm:space-y-4 h-[calc(90vh-80px)] sm:h-[calc(80vh-80px)] overflow-y-auto">
            {playingMedia.choreographyOnly ? (
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h4 className="text-base sm:text-lg font-semibold text-indigo-600 dark:text-indigo-400">Coreografia</h4>
                  {playingMedia.canDownload && playingMedia.choreographyId && playingMedia.sourceUrl && (
                    <button
                      type="button"
                      disabled={Boolean(downloadingMediaKey)}
                      onClick={() => downloadChoreographyUpload(
                        { id: playingMedia.choreographyId, title: playingMedia.title },
                        {
                          id: playingMedia.mediaId,
                          url: playingMedia.sourceUrl,
                          sourceType: playingMedia.sourceType || 'upload',
                          mimeType: playingMedia.mimeType || ''
                        }
                      )}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-200 dark:hover:bg-emerald-900/60"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      {downloadingMediaKey ? 'Download…' : 'Scarica'}
                    </button>
                  )}
                </div>
                <div className="aspect-video w-full h-[calc(70vh-100px)] sm:h-[calc(65vh-100px)]">
                  {playingMedia.type === 'youtube' ? (
                    <iframe
                      src={playingMedia.url}
                      title={playingMedia.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full rounded-lg"
                    ></iframe>
                  ) : playingMedia.type === 'video' ? (
                    <video controls className="w-full h-full rounded-lg">
                      <source src={playingMedia.url} type={playingMedia.mimeType || 'video/mp4'} />
                      Il tuo browser non supporta il tag video.
                    </video>
                  ) : (
                    <div className="w-full h-48 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-400 italic">
                      <a href={playingMedia.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline break-all">
                        {playingMedia.url}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ) : (
            <>
            {/* Sezione Canzone */}
            <div>
              <h4 className="text-base sm:text-lg font-semibold text-indigo-600 dark:text-indigo-400 mb-2">Canzone</h4>
              <div className="aspect-video w-full h-[calc(40vh-100px)] sm:h-[calc(45vh-100px)]">
                {playingMedia.type === 'youtube' ? (
                  <iframe
                    src={playingMedia.url}
                    title={playingMedia.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full rounded-lg"
                  ></iframe>
                ) : playingMedia.type === 'audio' ? (
                  <audio
                    src={playingMedia.url}
                    controls
                    className="w-full rounded-lg"
                  ></audio>
                ) : (
                  <video
                    src={playingMedia.url}
                    controls
                    className="w-full h-full rounded-lg"
                  ></video>
                )}
              </div>
            </div>

            {/* Sezione Coreografia (se presente) */}
            {playingMedia.choreography && (
              <div>
                <h4 className="text-base sm:text-lg font-semibold text-indigo-600 dark:text-indigo-400 mb-2">
                  Coreografia: {playingMedia.choreography.title} - {playingMedia.choreography.artist}
                </h4>
                <div className="aspect-video w-full h-[calc(40vh-100px)] sm:h-[calc(45vh-100px)]">
                  {playingMedia.choreography.type === 'youtube' ? (
                    <iframe
                      src={playingMedia.choreography.url}
                      title={playingMedia.choreography.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full rounded-lg shadow-md"
                    ></iframe>
                  ) : playingMedia.choreography.type === 'video' ? (
                    <video controls className="w-full h-full rounded-lg shadow-md">
                      <source src={playingMedia.choreography.url} type={playingMedia.choreography.mimeType || 'video/mp4'} />
                      Il tuo browser non supporta il tag video.
                    </video>
                  ) : (
                    <div className="w-full h-48 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center text-gray-500 dark:text-gray-400 italic">
                      Coreografia non disponibile o formato non supportato
                      <br/> URL: <a href={playingMedia.choreography.url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline break-all">
                        {playingMedia.choreography.url}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
            </>
            )}
          </div>
        )}
      </dialog>

      {/* Modals */}
      <dialog ref={editSongModalRef} className="mobile-sheet rounded-lg shadow-xl backdrop:bg-black backdrop:opacity-50 dark:bg-gray-800 dark:text-gray-200">
        <div className="mobile-sheet-panel bg-white dark:bg-gray-800">
        <MobileSheetHandle onClose={handleCancelEditSong} />
        <div className="mobile-sheet-header">
          <div className="flex items-start justify-between gap-3">
            <h3 className="section-title section-title--songs flex items-center">
              <svg className="w-6 h-6 mr-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
              {editingSong ? 'Modifica Canzone' : 'Aggiungi Canzone'}
            </h3>
            <button
              type="button"
              onClick={handleCancelEditSong}
              className="hidden shrink-0 rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white sm:inline-flex"
              aria-label="Chiudi"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
        <div className="mobile-sheet-body">
        <div className="grid grid-cols-1 gap-4 mb-4">
          <div>
            <label htmlFor="editSongTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titolo Canzone</label>
            <input
              id="editSongTitle"
              type="text"
              placeholder="Titolo Canzone"
              value={newSongTitle}
              onChange={(e) => {setNewSongTitle(e.target.value); setErrorMessage('');}}
              className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent transition duration-200 w-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
          </div>
          <div>
            <label htmlFor="editSongArtist" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Artista</label>
            <input
              id="editSongArtist"
              type="text"
              placeholder="Artista"
              value={newSongArtist}
              onChange={(e) => {setNewSongArtist(e.target.value); setErrorMessage('');}}
              className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent transition duration-200 w-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
          </div>
          <div>
            <label htmlFor="editSongGenre" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Genere</label>
            <select
              id="editSongGenre"
              value={newSongGenre}
              onChange={(e) => {setNewSongGenre(e.target.value); setErrorMessage('');}}
              className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent transition duration-200 w-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            >
              <option value="">Seleziona Genere</option>
              <option value="Country">Country</option>
              <option value="Pop">Pop</option>
              <option value="Rock">Rock</option>
              <option value="Classical">Classical</option>
              <option value="Jazz">Jazz</option>
              <option value="Blues">Blues</option>
              <option value="Electronic">Electronic</option>
              <option value="Hip Hop">Hip Hop</option>
              <option value="R&B">R&B</option>
              <option value="Folk">Folk</option>
              <option value="World">World</option>
              <option value="Other">Altro</option>
            </select>
          </div>
          <div>
            <label htmlFor="editSongUrl" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">URL Esterno</label>
            <input
              id="editSongUrl"
              type="url"
              placeholder="URL Esterno (es. Spotify, YouTube)"
              value={newSongUrl}
              onChange={(e) => {setNewSongUrl(e.target.value); setNewSongFile(null); setErrorMessage('');}}
              className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent transition duration-200 w-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
          </div>
          <div className="text-center text-gray-500 italic">O</div>
          <div>
            <label htmlFor="editSongFile" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Carica File Audio</label>
            <input
              id="editSongFile"
              type="file"
              accept="audio/mpeg, audio/wav, audio/ogg, audio/flac, .mp3, .wav, .ogg, .flac"
              onChange={(e) => {setNewSongFile(e.target.files[0]); setNewSongUrl(''); setErrorMessage('');}}
              className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent transition duration-200 w-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
            <p className="text-sm text-gray-500 italic mt-1">
              Seleziona un file audio (es. MP3, WAV, OGG, FLAC). Il browser filtrerà i file per tipo.
            </p>
          </div>
          {/* Campo Note per la Canzone (Modifica) */}
          <div>
            <label htmlFor="editSongNotes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note Canzone</label>
            <textarea
              id="editSongNotes"
              placeholder="Note aggiuntive sulla canzone (es. tonalità, struttura)"
              value={newSongNotes}
              onChange={(e) => setNewSongNotes(e.target.value)}
              rows="3"
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent transition duration-200 w-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            ></textarea>
          </div>
           {/* Progress bar per l'upload della canzone nel modale di modifica */}
          {uploadingSong && songUploadProgress > 0 && (
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 mt-2">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{ width: `${songUploadProgress}%` }}
              ></div>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-1 block">
                Caricamento: {songUploadProgress}%
              </span>
            </div>
          )}
        </div>
        </div>
        <div className="mobile-sheet-footer">
          <button
            type="button"
            onClick={handleCancelEditSong}
            className="modal-btn modal-btn-ghost"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={handleSaveSong}
            className="modal-btn modal-btn-primary"
            disabled={uploadingSong}
          >
            {uploadingSong ? (
              <>
                <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Salvataggio...
              </>
            ) : 'Salva'}
          </button>
        </div>
        </div>
      </dialog>

      <dialog ref={editChoreographyModalRef} className="mobile-sheet rounded-lg shadow-xl backdrop:bg-black backdrop:opacity-50 dark:bg-gray-800 dark:text-gray-200">
        <div className="mobile-sheet-panel bg-white dark:bg-gray-800">
        <MobileSheetHandle onClose={handleCancelEditChoreography} />
        <div className="mobile-sheet-header">
          <div className="flex items-start justify-between gap-3">
            <h3 className="section-title section-title--choreo flex items-center">
              <svg className="w-6 h-6 mr-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
              {editingChoreography ? 'Modifica Coreografia' : 'Aggiungi Coreografia'}
            </h3>
            <button
              type="button"
              onClick={handleCancelEditChoreography}
              className="hidden shrink-0 rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white sm:inline-flex"
              aria-label="Chiudi"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          {errorMessage && (
            <div className="mt-3 rounded-lg border border-red-300 bg-red-100 px-3 py-2 text-sm font-semibold text-red-800 dark:border-red-700 dark:bg-red-900/50 dark:text-red-100">
              {errorMessage}
            </div>
          )}
        </div>
        <div className="mobile-sheet-body">
        <div className="grid grid-cols-1 gap-4 mb-4">
          <div>
            <label htmlFor="editChoreographyTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titolo Coreografia</label>
            <input
              id="editChoreographyTitle"
              type="text"
              placeholder="Titolo Coreografia"
              value={newChoreographyTitle}
              onChange={(e) => {setNewChoreographyTitle(e.target.value); setErrorMessage('');}}
              className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent transition duration-200 w-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
          </div>
          <div>
            <label htmlFor="editChoreographyArtist" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Coreografo/Artista</label>
            <input
              id="editChoreographyArtist"
              type="text"
              placeholder="Coreografo/Artista"
              value={newChoreographyArtist}
              onChange={(e) => {setNewChoreographyArtist(e.target.value); setErrorMessage('');}}
              className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent transition duration-200 w-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
          </div>
          <div>
            <label htmlFor="editChoreographyDifficulty" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Difficoltà</label>
            <select
              id="editChoreographyDifficulty"
              value={newChoreographyDifficulty}
              onChange={(e) => {setNewChoreographyDifficulty(e.target.value); setErrorMessage('');}}
              className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent transition duration-200 w-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            >
              <option value="">Seleziona Difficoltà</option>
              <option value="Facile">Facile</option>
              <option value="Medio">Medio</option>
              <option value="Difficile">Difficile</option>
            </select>
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Media (link e/o upload)</label>
              <button
                type="button"
                onClick={() => setChoreographyMediaItems((prev) => [...prev, createEmptyChoreographyMediaItem()])}
                className="text-sm font-semibold text-green-700 hover:text-green-800 dark:text-green-300"
              >
                + Aggiungi media
              </button>
            </div>
            <div className="space-y-3">
              {choreographyMediaItems.map((item, index) => (
                <div key={item.clientId} className="rounded-lg border border-gray-300 p-3 dark:border-gray-600">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Media {index + 1}</span>
                    {choreographyMediaItems.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          if (item.id && !String(item.id).startsWith('legacy-')) {
                            setRemovedChoreographyMediaIds((prev) => [...prev, item.id]);
                          }
                          setChoreographyMediaItems((prev) => prev.filter((x) => x.clientId !== item.clientId));
                        }}
                        className="text-xs font-semibold text-red-600 hover:text-red-700"
                      >
                        Rimuovi
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    placeholder="Etichetta (opzionale)"
                    value={item.label}
                    onChange={(e) => {
                      const value = e.target.value;
                      setChoreographyMediaItems((prev) => prev.map((x) => x.clientId === item.clientId ? { ...x, label: value } : x));
                    }}
                    className="mb-2 w-full rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                  />
                  <input
                    type="url"
                    placeholder="URL video esterno (es. YouTube, Vimeo)"
                    value={item.url}
                    onChange={(e) => {
                      const value = e.target.value;
                      setChoreographyMediaItems((prev) => prev.map((x) => x.clientId === item.clientId ? { ...x, url: value, file: null } : x));
                      setErrorMessage('');
                    }}
                    className="mb-2 w-full rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                  />
                  <div className="mb-1 text-center text-xs italic text-gray-500">oppure</div>
                  <input
                    type="file"
                    accept="video/mp4, video/webm, video/ogg, video/mov, .mp4, .webm, .ogg, .mov"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setChoreographyMediaItems((prev) => prev.map((x) => x.clientId === item.clientId ? { ...x, file, url: file ? '' : x.url } : x));
                      setErrorMessage('');
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                  />
                  {item.file && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">File selezionato: {item.file.name}</p>
                  )}
                  {!item.file && item.url && item.id && (
                    <p className="mt-1 text-xs text-gray-500 break-all dark:text-gray-400">Salvato: {item.url}</p>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-2 text-sm italic text-gray-500">
              Puoi aggiungere piu link e piu file video alla stessa coreografia.
            </p>
          </div>
          {/* Campo Note per la Coreografia (Modifica) */}
          <div>
            <label htmlFor="editChoreographyNotes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note Coreografia</label>
            <textarea
              id="editChoreographyNotes"
              placeholder="Note aggiuntive sulla coreografia (es. focus su movimenti specifici)"
              value={newChoreographyNotes}
              onChange={(e) => setNewChoreographyNotes(e.target.value)}
              rows="3"
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent transition duration-200 w-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            ></textarea>
          </div>
          {/* Progress bar per l'upload della coreografia nel modale di modifica */}
          {uploadingChoreography && (
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 mt-2">
              <div
                className="bg-green-600 h-2.5 rounded-full transition-all"
                style={{ width: `${Math.max(choreoUploadProgress, 5)}%` }}
              ></div>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-1 block">
                Caricamento: {choreoUploadProgress > 0 ? `${choreoUploadProgress}%` : 'avvio...'}
              </span>
            </div>
          )}
        </div>
        </div>
        <div className="mobile-sheet-footer">
          <button
            type="button"
            onClick={handleCancelEditChoreography}
            className="modal-btn modal-btn-ghost"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={handleSaveChoreography}
            className="modal-btn modal-btn-success"
            disabled={uploadingChoreography}
          >
            {uploadingChoreography ? (
              <>
                <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Salvataggio...
              </>
            ) : 'Salva'}
          </button>
        </div>
      </div>
      </dialog>

      <dialog ref={newAssociationModalRef} className="mobile-sheet rounded-lg shadow-xl backdrop:bg-black backdrop:opacity-50 dark:bg-gray-800 dark:text-gray-200 w-11/12 md:w-1/2 lg:w-2/5 max-w-2xl">
        <div className="mobile-sheet-panel bg-white dark:bg-gray-800 p-4 sm:p-6">
        {errorMessage && (
          <div className="mb-4 rounded-lg border border-red-300 bg-red-100 px-3 py-2 text-sm font-semibold text-red-800 dark:border-red-700 dark:bg-red-900/50 dark:text-red-100">
            {errorMessage}
          </div>
        )}
        <h3 className="text-2xl font-bold text-indigo-700 dark:text-indigo-400 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          Nuova Associazione
        </h3>
        <div className="grid grid-cols-1 gap-4 mb-4">
          <div>
            <label htmlFor="newAssociationChoreography" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Coreografia</label>
            <select
              id="newAssociationChoreography"
              value={selectedChoreographyId}
              onChange={(e) => { setSelectedChoreographyId(e.target.value); setErrorMessage(''); }}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            >
              <option value="">Seleziona una coreografia...</option>
              {choreographies.map((choreo) => (
                <option key={choreo.id} value={choreo.id}>
                  {choreo.title} - {choreo.artist} ({choreo.difficulty || 'N/D'})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="newAssociationSongs" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Canzoni</label>
            <select
              id="newAssociationSongs"
              multiple
              value={selectedSongIds}
              onChange={(e) => { setSelectedSongIds(Array.from(e.target.selectedOptions, (option) => option.value)); setErrorMessage(''); }}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              size="5"
            >
              {songs.map((song) => (
                <option key={song.id} value={song.id}>
                  {song.title} - {song.artist} ({song.genre || 'N/D'})
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Tieni premuto Ctrl (o Cmd su Mac) per selezionare più canzoni</p>
          </div>
          <div>
            <label htmlFor="newAssociationNotes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note (opzionale)</label>
            <textarea
              id="newAssociationNotes"
              value={newAssociationNotes}
              onChange={(e) => setNewAssociationNotes(e.target.value)}
              rows="3"
              placeholder="Aggiungi note all'associazione..."
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            />
          </div>
        </div>
        <div className="modal-actions">
          <button
            type="button"
            onClick={handleCancelNewAssociation}
            className="modal-btn modal-btn-ghost"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={addAssociations}
            className="modal-btn modal-btn-accent"
          >
            Crea
          </button>
        </div>
      </div>
      </dialog>

      {/* Modals */}
      <dialog ref={editAssociationModalRef} className="mobile-sheet rounded-lg shadow-xl bg-white dark:bg-gray-800 max-w-2xl w-full">
        <div className="mobile-sheet-panel bg-white dark:bg-gray-800 p-4 sm:p-6">
        <h3 className="text-xl font-bold mb-4 text-indigo-700 dark:text-indigo-400">Modifica Associazione</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Selezione Coreografia */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Coreografia</label>
            <select
              value={editingAssociation?.choreographyId || ''}
              onChange={(e) => setEditingAssociation(prev => ({ ...prev, choreographyId: e.target.value }))}
              className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition duration-200 w-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            >
              <option value="">Seleziona una coreografia</option>
              {choreographies.map((choreo) => (
                <option key={choreo.id} value={choreo.id}>
                  {choreo.title} - {choreo.artist} (Difficoltà: {choreo.difficulty || 'N/D'})
                </option>
              ))}
            </select>
          </div>

          {/* Selezione Canzoni */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Canzoni</label>
            <select
              multiple
              value={editingAssociation?.songIds || []}
              onChange={(e) => setEditingAssociation(prev => ({ ...prev, songIds: Array.from(e.target.selectedOptions, option => option.value) }))}
              className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition duration-200 w-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              size="5"
            >
              {songs.map((song) => (
                <option key={song.id} value={song.id}>
                  {song.title} - {song.artist} ({song.genre || 'N/D'})
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Tieni premuto Ctrl (o Cmd su Mac) per selezionare più canzoni</p>
          </div>
        </div>

        {/* Note */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note</label>
          <textarea
            value={editingAssociation?.notes || ''}
            onChange={(e) => setEditingAssociation(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Inserisci note per l'associazione"
            className="p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition duration-200 w-full bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
            rows="3"
          />
        </div>

        {/* Pulsanti */}
        <div className="mt-4 modal-actions">
          <button
            onClick={handleCancelEditAssociation}
            className="modal-btn modal-btn-ghost"
          >
            Annulla
          </button>
          <button
            onClick={handleSaveAssociation}
            className="modal-btn modal-btn-accent"
          >
            Salva
          </button>
        </div>
      </div>
      </dialog>

      {/* Modal di conferma eliminazione */}
      <dialog ref={confirmModalRef} className="mobile-sheet rounded-lg shadow-xl bg-white dark:bg-gray-800 max-w-md w-full">
        <div className="mobile-sheet-panel bg-white dark:bg-gray-800 p-4 sm:p-6">
        <h3 className="text-xl font-bold mb-4 text-red-600 dark:text-red-400">Conferma Eliminazione</h3>
        <p className="text-gray-700 dark:text-gray-300 mb-6">
          Sei sicuro di voler eliminare questo elemento? Questa azione non può essere annullata.
        </p>
        <div className="modal-actions">
          <button
            onClick={() => {
              if (confirmModalRef.current) {
                confirmModalRef.current.close();
              }
              setItemToDelete(null);
            }}
            className="modal-btn modal-btn-ghost"
          >
            Annulla
          </button>
          <button
            onClick={() => deleteItem(itemToDelete?.type, itemToDelete?.id)}
            className="modal-btn modal-btn-danger"
          >
            Elimina
          </button>
        </div>
      </div>
      </dialog>

      <dialog
        ref={shareRecipientModalRef}
        className="mobile-sheet rounded-lg shadow-xl backdrop:bg-black backdrop:opacity-50 dark:bg-gray-800 dark:text-gray-200 max-w-md w-[95vw] sm:w-full"
        onClose={closeShareRecipientModal}
        onCancel={(e) => {
          e.preventDefault();
          closeShareRecipientModal();
        }}
      >
        <div className="mobile-sheet-panel bg-white dark:bg-gray-800">
          <MobileSheetHandle onClose={closeShareRecipientModal} />
          <div className="mobile-sheet-header">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-indigo-700 dark:text-indigo-400">Invia a utente</h3>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  {selectedShareCount} element{selectedShareCount === 1 ? 'o' : 'i'}: C {cloneSelectedSongIds.length} · Co {cloneSelectedChoreoIds.length} · A {cloneSelectedAssociationIds.length}
                </p>
              </div>
              <button
                type="button"
                onClick={closeShareRecipientModal}
                className="inline-flex shrink-0 rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                aria-label="Chiudi"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <div className="mobile-sheet-body">
            {errorMessage && (
              <div className="mb-3 rounded-xl border border-red-300 bg-red-100 px-3 py-2 text-sm font-semibold text-red-800 dark:border-red-700 dark:bg-red-900/50 dark:text-red-100">
                {errorMessage}
              </div>
            )}
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Username destinatario</label>
            <input
              type="text"
              value={cloneTargetUsername}
              onChange={(e) => setCloneTargetUsername(e.target.value)}
              placeholder="es. mario.rossi"
              className="mb-3 w-full rounded-lg border border-gray-300 bg-white p-3 text-base text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              autoComplete="username"
            />

            <div className="mb-2 space-y-2">
              {cloneSelectedAssociationIds.length > 0 && (
                <label className="flex items-center text-xs text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={cloneIncludeDependencies}
                    onChange={(e) => setCloneIncludeDependencies(e.target.checked)}
                    className="mr-2"
                  />
                  Includi canzoni/coreografie collegate
                </label>
              )}
              <label className="flex items-center text-xs text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={cloneCopyFiles}
                  onChange={(e) => setCloneCopyFiles(e.target.checked)}
                  className="mr-2"
                />
                Duplica anche i file locali
              </label>
              <label className="flex items-center text-xs text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={cloneSkipExisting}
                  onChange={(e) => setCloneSkipExisting(e.target.checked)}
                  className="mr-2"
                />
                Salta elementi già esistenti
              </label>
            </div>

            {cloneReport && (
              <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs dark:border-gray-600 dark:bg-gray-900">
                <p><strong>Condivisi</strong> S:{cloneReport.copied?.songs || 0} C:{cloneReport.copied?.choreographies || 0} A:{cloneReport.copied?.associations || 0} F:{cloneReport.copied?.files || 0}</p>
                <p><strong>Saltati</strong> S:{cloneReport.skipped?.songs || 0} C:{cloneReport.skipped?.choreographies || 0} A:{cloneReport.skipped?.associations || 0}</p>
                <p><strong>Da</strong>: {cloneReport.sourceUser?.username} | <strong>A</strong>: {cloneReport.targetUser?.username}</p>
              </div>
            )}
          </div>
          <div className="mobile-sheet-footer">
            <button
              type="button"
              onClick={closeShareRecipientModal}
              className="modal-btn modal-btn-ghost"
            >
              Chiudi
            </button>
            <button
              type="button"
              onClick={() => handleCloneUserData(false)}
              disabled={cloneLoading}
              className="modal-btn modal-btn-primary"
            >
              Anteprima
            </button>
            <button
              type="button"
              onClick={() => handleCloneUserData(true)}
              disabled={cloneLoading}
              className="modal-btn modal-btn-success"
            >
              Invia
            </button>
          </div>
        </div>
      </dialog>

      {/* Modal dei termini */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full">
            <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Termini e Condizioni</h2>
            <div className="prose dark:prose-invert max-h-96 overflow-y-auto mb-4">
              <p>Per utilizzare questa applicazione, è necessario accettare i seguenti termini e condizioni:</p>
              <ul>
                <li>Rispettare i diritti d'autore per i contenuti musicali</li>
                <li>Utilizzare i contenuti solo per scopi personali</li>
                <li>Non condividere i contenuti con terze parti</li>
                <li>Mantenere la riservatezza delle tue credenziali</li>
              </ul>
              {errorMessage && (
                <div className="mt-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded-lg">
                  {errorMessage}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowTermsModal(false);
                  handleLogout();
                }}
                className="modal-btn modal-btn-ghost"
              >
                Rifiuta
              </button>
              <button
                type="button"
                onClick={handleAcceptTerms}
                disabled={isSavingTerms}
                className="modal-btn modal-btn-primary"
              >
                {isSavingTerms ? 'Salvataggio…' : 'Accetta'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showRegistrationResetCode ? (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900 rounded-lg border border-yellow-200 dark:border-yellow-800 mt-4 text-center">
          <p className="text-yellow-800 dark:text-yellow-200 font-semibold mb-2">
            Registrazione completata! Copia e conserva il codice qui sotto: ti servirà per recuperare la password.
          </p>
          <div className="text-2xl font-bold text-yellow-900 dark:text-yellow-100 text-center select-all mb-2">{registrationResetCode}</div>
          <p className="text-xs text-yellow-700 dark:text-yellow-300 mb-4">Conserva questo codice in un luogo sicuro! Senza di esso non potrai recuperare la password.</p>
          <button
            type="button"
            className="w-full bg-blue-500 text-white py-2.5 px-4 rounded-xl hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 ease-in-out shadow-sm"
            onClick={() => {
              setShowRegistrationResetCode(false);
              setIsRegistering(false);
            }}
          >
            Ho copiato il codice
          </button>
        </div>
      ) : (
        <>
          {errorMessage && (
            <div className="mb-4 p-3 rounded bg-red-100 text-red-700 border border-red-300 font-semibold text-center">
              {errorMessage}
            </div>
          )}
        </>
      )}

      {/* Disclaimer Modal - ora fuori dal menu laterale */}
      {showDisclaimer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full shadow-lg">
            <h3 className="text-lg font-semibold text-indigo-600 dark:text-indigo-400 mb-2">Disclaimer</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              Questo software è fornito "così com'è", senza garanzie di alcun tipo.<br/>
              L'autore non è responsabile per eventuali danni derivanti dall'uso di questo software.<br/>
              <br/>
              <strong>Scarico di responsabilità:</strong><br/>
              - L'utente è l'unico responsabile del caricamento di file e contenuti multimediali. È vietato caricare materiale protetto da diritti d'autore senza autorizzazione.<br/>
              - L'autore e i gestori dell'applicazione non sono responsabili per eventuali violazioni di copyright commesse dagli utenti.<br/>
              - L'autore non garantisce la conservazione, l'integrità o la sicurezza dei dati caricati. Eventuali perdite di dati, cancellazioni accidentali o malfunzionamenti non sono imputabili all'autore.<br/>
              - L'applicazione è destinata esclusivamente a scopo personale e didattico. L'uso improprio, la condivisione non autorizzata o la distribuzione dei contenuti caricati è vietata.<br/>
              - Utilizzando questa applicazione, l'utente accetta integralmente queste condizioni e solleva l'autore da qualsiasi responsabilità civile, penale o amministrativa derivante dall'uso dell'applicazione stessa.<br/>
            </p>
            <div className="mt-4 modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn-accent"
                onClick={() => setShowDisclaimer(false)}
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {showProfileModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-5 w-[92vw] max-w-md shadow-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-indigo-700 dark:text-indigo-300">Come sei loggato</h3>
              <button
                type="button"
                onClick={() => setShowProfileModal(false)}
                className="text-gray-500 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white"
                aria-label="Chiudi profilo"
              >
                ✕
              </button>
            </div>
            <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
              <div className="p-3 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                <p><strong>Username:</strong> {user?.username || '-'}</p>
                <p><strong>ID utente:</strong> {user?.id || '-'}</p>
                <p><strong>Email:</strong> {user?.email || '-'}</p>
                <p><strong>Termini accettati:</strong> {termsAccepted ? 'Sì' : 'No'}</p>
                <p><strong>Ultima accettazione termini:</strong> {termsLastAccepted ? new Date(termsLastAccepted).toLocaleString('it-IT') : 'N/D'}</p>
              </div>
            </div>
            <div className="mt-4 modal-actions">
              <button
                type="button"
                onClick={() => setShowProfileModal(false)}
                className="modal-btn modal-btn-ghost"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      <dialog
        ref={trainingModalRef}
        className="mobile-sheet rounded-lg shadow-xl backdrop:bg-black backdrop:opacity-50 dark:bg-gray-800 dark:text-gray-200"
        onClose={closeTraining}
      >
        <div className="mobile-sheet-panel bg-white dark:bg-gray-800">
          <MobileSheetHandle onClose={closeTraining} />
          <div className="mobile-sheet-header">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-xl sm:text-2xl font-bold text-indigo-700 dark:text-indigo-400">Training</h3>
              <button
                type="button"
                onClick={closeTraining}
                className="hidden shrink-0 rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white sm:inline-flex"
                aria-label="Chiudi"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
          <div className="mobile-sheet-body">
            {!trainingStarted ? (
              <form onSubmit={startTraining} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Quante coreografie vuoi estrarre?</label>
                  <input
                    type="number"
                    min="1"
                    max={Math.max(1, choreographies.length)}
                    value={trainingCount}
                    onChange={e => setTrainingCount(e.target.value)}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Difficoltà</label>
                  <select
                    value={trainingDifficulty}
                    onChange={e => setTrainingDifficulty(e.target.value)}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  >
                    <option value="all">Tutte</option>
                    <option value="Facile">Facile</option>
                    <option value="Media">Media</option>
                    <option value="Difficile">Difficile</option>
                  </select>
                </div>
              </form>
            ) : (
              <div>
                <h4 className="text-base font-semibold mb-3 text-gray-800 dark:text-gray-100">Coreografie estratte</h4>
                {trainingChoreographies.length === 0 ? (
                  <p className="text-sm text-red-500">Nessuna coreografia trovata con questi criteri.</p>
                ) : (
                  <ul className="space-y-2">
                    {trainingChoreographies.map((choreo) => (
                      <li key={choreo.id} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-700/60">
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-semibold text-gray-900 dark:text-gray-100">{choreo?.title}</p>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-300">{choreo?.artist} · {choreo?.difficulty || 'N/D'}</p>
                        </div>
                        <button type="button" onClick={() => handleTrainingPlay(choreo)} className="modal-btn modal-btn-success shrink-0">Avvia</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <div className="mobile-sheet-footer">
            {!trainingStarted ? (
              <>
                <button type="button" onClick={closeTraining} className="modal-btn modal-btn-ghost">Annulla</button>
                <button type="button" onClick={startTraining} className="modal-btn modal-btn-accent">Avvia</button>
              </>
            ) : (
              <>
                <button type="button" onClick={() => { setTrainingStarted(false); setTrainingChoreographies([]); }} className="modal-btn modal-btn-ghost">Nuova</button>
                <button type="button" onClick={closeTraining} className="modal-btn modal-btn-ghost">Chiudi</button>
              </>
            )}
          </div>
        </div>
      </dialog>
    </div>
  );
}

export default App;
